/**
 * The English label catalogue — the single source of every user-facing string
 * (R18.9).
 *
 * R18.9 requires every interface label, validation message and export column
 * label to be read from an externalized resource file rather than hardcoded in a
 * component or a rule. This object is that file for English, the only locale
 * Foundation ships. Three things live here, and nothing user-facing lives outside
 * here:
 *
 * 1. **Interface labels** — everything the shell, the calendar, the filter bar,
 *    the saved-view menu, the detail panel and the error surface render.
 * 2. **Validation messages** — one entry per `messageKey` the domain and API
 *    emit on a finding. A finding never carries user-facing text; it carries a
 *    `messageKey` (see `packages/domain` `RuleFinding.messageKey`) that resolves
 *    here (R18.16). The keys under `validation.*`, `session.ended.*` and
 *    `platformSet.*`/`platformColor.*`/etc. are exactly those the registries emit,
 *    so a finding's key always resolves to text — and the source-literal lint
 *    rule guarantees no rule ever inlines the text instead.
 * 3. **Export column labels** — the 21 R15.1 filtered-view columns
 *    (`export.column.*`). The workbook generator names the same columns; this
 *    catalogue is the client-visible source of their labels so the two never
 *    drift, per design.md ("All user-facing strings, validation messages and
 *    export column labels come from i18next resource files").
 *
 * The nesting uses i18next's default `.` key separator, so a dotted message key
 * such as `validation.title.length` resolves through the nested object below with
 * no special handling.
 */

export const CATALOGUE_LANGUAGE = 'en';

/** The single i18next namespace Foundation uses; declared once so callers stay consistent. */
export const CATALOGUE_NAMESPACE = 'translation';

export const en = {
  app: {
    name: 'ComPlan',
    eyebrow: 'Commercial Planning',
    footer: 'ComPlan — MVP 1',
  },

  // The "made by humai." badge, fixed bottom right on every page, and its info
  // pop-up (components/layout/HumaiBadge.tsx).
  humai: {
    badgeAlt: 'made by humai.',
    popupLabel: 'About humai',
    logoAlt: 'humai.',
    claim: 'Human thinking. Intelligent doing.',
    tagline: 'Thoughtfully built to make everyday work easier.',
    credits: 'Build by: Eric Fritzsche',
    support: 'Support & Feedback',
    teams: 'Chat in Teams',
  },

  nav: {
    ariaLabel: 'Primary',
    group: {
      plan: 'Plan',
      manpower: 'Manpower',
      maintain: 'Maintain',
      govern: 'Govern',
      configure: 'Configure',
    },
    item: {
      dashboard: 'Dashboard',
      commercialCalendar: 'Commercial Calendar',
      initiatives: 'Initiatives',
      capacityUtilisation: 'Capacity & Utilisation',
      demandExplorer: 'Demand Explorer',
      capacityInputs: 'Capacity Inputs',
      effortMatrix: 'Effort Matrix',
      initiativeIntake: 'Initiative Intake',
      salesCycleTiming: 'Sales Cycle Timing',
      governance: 'Governance',
      reportsExports: 'Reports & Exports',
      administration: 'Administration',
    },
    placeholderHint: 'Available in a later release',
  },

  common: {
    // The generic failure heading every screen's destructive alert uses. It was missing
    // from this catalogue while nine call sites already read it, so each of them rendered
    // the literal key `common.error` as its alert title - the exact class of gap task 18.5
    // exists to close.
    error: 'Something went wrong',
    reset: 'Reset',
    reload: 'Reload',
    retry: 'Retry',
    save: 'Save',
    loading: 'Loading…',
    any: 'Any',
    yes: 'Yes',
    no: 'No',
    account: 'Account',
    signedInAs: 'Signed in as {{subject}}',
  },

  // The shared inline date-range picker (`shared/components/InlineDateRangePicker`).
  // Weekday headers start at Monday, per ISO 8601 (assumption A5).
  datePicker: {
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    today: 'Today',
    clearSelection: 'Clear selection',
    // The closed-state trigger field's placeholder (`DateRangeField`) when no
    // range is selected yet.
    rangePlaceholder: 'Select dates',
    weekday: {
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat',
      sun: 'Sun',
    },
  },

  page: {
    initiatives: {
      title: 'Initiatives',
      subtitle: 'Every initiative you may read, in any planning year.',
    },
    intake: {
      title: 'Initiative Intake',
      note: 'The intake wizard lands in tasks 23.1–23.2.',
    },
    salesCycleTiming: {
      title: 'Sales Cycle Timing',
      note: 'The Sales Cycle timing form lands in task 23.3.',
    },
    reports: {
      title: 'Reports & Exports',
      note: 'The export screen lands in task 23.5.',
    },
    admin: {
      title: 'Administration',
      note: 'Master-data and role administration lands in task 23.6.',
    },
    governance: {
      title: 'Governance',
      subtitle: 'Readiness, watchouts, dependencies and data quality across what you can read.',
      filterBarLabel: 'Governance dashboard scope',
      noAccess: 'You do not have access to the governance dashboard.',
      loadError: 'The governance figures could not be read.',
    },
    dashboard: {
      title: 'Dashboard',
      subtitle: 'The planning year at a glance.',
      filterBarLabel: 'Management dashboard scope',
      noAccess: 'You do not have access to this dashboard.',
      loadError: 'The dashboard figures could not be read.',
    },
    notFound: {
      title: 'Not found',
      note: 'This screen does not exist.',
    },
  },

  topBar: {
    newInitiative: 'New initiative',
    signOut: 'Sign out',
    signingOut: 'Signing out…',
    signOutFailed: 'Sign-out failed. Try again.',
  },

  error: {
    reference: 'Reference: {{correlationId}}',
    signIn: 'Sign in',
    unauthenticated: {
      title: 'Sign-in required',
      description: 'Your session has ended. Sign in again to continue.',
    },
    forbidden: {
      title: 'Not permitted',
      description: 'You do not have access to this action.',
    },
    notFound: {
      title: 'Not found',
      description: 'The requested item is not available.',
    },
    validationFailed: {
      title: 'Could not be saved',
      description: 'Some values need attention before this can be saved.',
    },
    conflict: {
      title: 'Changed elsewhere',
      description:
        'This record changed since you loaded it. Review the latest values and try again.',
    },
    internal: {
      title: 'Something went wrong',
      description: 'An unexpected error occurred. The issue has been logged.',
    },
  },

  calendar: {
    title: 'Commercial Calendar',
    subtitle: 'Planning year {{year}} · {{orientation}}',
    laneHeader: 'Platform / lane',
    salesCycleBandHeader: 'Sales cycle',
    milestoneSwimlaneHeader: 'Milestones',
    loading: 'Loading the {{orientation}} calendar…',
    loadError: 'The calendar could not be loaded.',
    empty: 'No initiative matches the current selection. Showing an empty {{year}} calendar.',
    resultCount_one: '{{count}} initiative',
    resultCount_other: '{{count}} initiatives',
    currentPeriod: 'current period',
    orientation: {
      group: 'Calendar orientation',
      month: 'Month',
      calendarWeek: 'Calendar week',
      salesCycle: 'Sales cycle',
      monthLower: 'month',
      calendarWeekLower: 'calendar week',
      salesCycleLower: 'sales cycle',
    },
    export: {
      preparing: 'Preparing your export…',
      ready: 'Your export is ready to download.',
      timedOut: 'The export timed out. Please try a narrower filter.',
      failed: 'The export failed. Please try again.',
      startFailed: 'The export could not be started. Please try again.',
    },
  },

  legend: {
    ariaLabel: 'Calendar legend',
    platforms: 'Platforms:',
    status: 'Status:',
    markers: 'Markers:',
    // One entry per status of R9.1, describing how that status paints. The bar's
    // fill is the Platform colour, so each status is told apart by the word it
    // carries, its fill treatment, its opacity or its title treatment — never by hue
    // alone (§4.5, R10.6). The three pre-approval statuses share a pale fill and are
    // marked by a dashed outline, nothing, or a soft hatch; the rest are solid. The
    // wording names the pale/solid split first because that is the legible one.
    statusTreatment: {
      draft: '— labelled, pale with a dashed outline, faded',
      submitted: '— labelled, pale, faded',
      planned: '— labelled, pale with a soft hatch, faded',
      approved: '— labelled, solid, full strength',
      final: '— labelled, solid, white inner border',
      cancelled: '— labelled, solid grey, struck through',
    },
    milestone: 'Milestone',
    elevatedPriority: 'Elevated priority',
    confidential: 'Confidential',
    currentPeriod: 'Current period',
  },

  filter: {
    searchLabel: 'Calendar filters',
    year: 'Year',
    export: 'Export',
    exporting: 'Exporting…',
    resultCountVisible_one: '{{count}} initiative visible',
    resultCountVisible_other: '{{count}} initiatives visible',
    removeChip: 'Remove filter {{label}}',
    chip: {
      value: '{{label}}: {{value}}',
    },
    searchPlaceholder: 'ID, title, owner, platform…',
    noCycles: 'No cycles',
    multiSelectAny: 'Any',
    // Marks an option offered only because a filter or a Saved_View still names it
    // after it was deactivated (R11.2, R11.15).
    inactiveMarker: '(inactive)',
    // The disclosure holding the dimensions R11.2-R11.6 require beyond the eight
    // UI spec §3.6 fixes as the calendar's binding set.
    moreDimensions: 'More filters',
    multiSelectCount: '{{count}} selected',
    booleanYes: 'yes',
    booleanNo: 'no',
    dimension: {
      salesCycleIds: 'Sales cycle',
      months: 'Month',
      isoWeeks: 'Calendar week',
      platformIds: 'Platform',
      channelIds: 'Channel',
      customerGroupIds: 'Customer group',
      audienceIds: 'Audience',
      categoryIds: 'Category',
      initiativeTypeIds: 'Initiative type',
      ownerUserIds: 'Owner',
      statuses: 'Status',
      priorityIds: 'Priority',
      fieldForceRoles: 'FF role',
      confidential: 'Confidential',
      fieldForceInvolvement: 'FF involvement',
      fieldForceMaterial: 'FF material',
      hasWorkOrder: 'Work order',
      hasInformationContribution: 'Information contribution',
      dteInvolvement: 'DTE involvement',
      cscInvolvement: 'CSC involvement',
      openDependencies: 'Open dependencies',
      // `complan-governance` R6.11, R6.12.
      hasUnresolvedWatchout: 'Unresolved watchout',
      watchoutSeverities: 'Watchout severity',
      milestoneReadiness: 'Milestone readiness',
      search: 'Search',
      planningYear: 'Planning year',
    },
  },

  governance: {
    /**
     * The Metric_Registry's text, in the keys the registry declares
     * (`complan-governance` R2.1, R2.3, R2.8).
     *
     * Every entry is `governance.metric.<id>.label`, `.numerator` and — for a share —
     * `.denominator`, because the registry in `@complan/domain` builds those keys from
     * the metric id. R2.8 requires the whole definition to travel through i18n, so a
     * screen never writes a metric's name itself.
     *
     * `numerator` is phrased as "what is counted", and `denominator` as "out of what",
     * so the two read as a sentence in the tile's definition panel. A share's two lines
     * have to be different sets or the figure would always be 100 percent, and stating
     * them separately is what makes that checkable by a reader (R2.3).
     */
    metric: {
      // --- counts (R3.2-R3.7, R4.2, R4.4) ---
      initiative_count: {
        label: 'Initiatives',
        numerator: 'Initiatives you can read that match the current selection',
      },
      readiness_complete_initiative_count: {
        label: 'Readiness complete',
        numerator: 'Initiatives whose every required milestone is completed',
      },
      readiness_overdue_initiative_count: {
        label: 'Overdue milestones',
        numerator:
          'Initiatives holding at least one required milestone planned before the cut-off date and not completed',
      },
      readiness_due_initiative_count: {
        label: 'Milestones due soon',
        numerator:
          'Initiatives holding at least one required milestone planned within the readiness horizon and not completed',
      },
      readiness_missing_initiative_count: {
        label: 'Milestone dates missing',
        numerator: 'Initiatives holding a required milestone with no planned date recorded',
      },
      readiness_not_applicable_initiative_count: {
        label: 'No required milestones',
        numerator:
          'Initiatives whose milestone types carry no mandatory rule, so readiness does not apply',
      },
      changed_after_approval_initiative_count: {
        label: 'Changed after approval',
        numerator: 'Initiatives changed after the approval that is currently in force',
      },
      unresolved_watchout_initiative_count: {
        label: 'With open watchouts',
        numerator: 'Initiatives holding at least one unresolved watchout',
      },
      unresolved_watchout_count: {
        label: 'Open watchouts',
        // Counts watchouts, not Initiatives — which is why this tile offers no
        // drill-through to an Initiative list (see `metric-drill-through.ts`).
        numerator: 'Unresolved watchouts across the Initiatives you can read',
      },
      high_severity_unresolved_watchout_initiative_count: {
        label: 'High-severity watchouts',
        numerator: 'Initiatives holding at least one unresolved watchout of high severity',
      },
      dte_involvement_initiative_count: {
        label: 'DTE involved',
        numerator: 'Initiatives recording digital technology involvement',
      },
      csc_involvement_initiative_count: {
        label: 'CSC involved',
        numerator: 'Initiatives recording customer service centre involvement',
      },
      open_dependency_initiative_count: {
        label: 'With open dependencies',
        numerator: 'Initiatives holding at least one dependency that is not complete',
      },
      overdue_dependency_count: {
        label: 'Overdue dependencies',
        numerator: 'Open dependencies whose due date precedes the cut-off date',
      },
      governance_exception_initiative_count: {
        label: 'Governance exceptions',
        numerator: 'Initiatives leaving at least one governance-tier validation rule unmet',
      },
      warning_exception_initiative_count: {
        label: 'Warning exceptions',
        numerator: 'Initiatives leaving at least one warning-tier validation rule unmet',
      },
      ownership_gap_initiative_count: {
        label: 'Ownership gaps',
        numerator:
          'Initiatives whose owner is inactive, or committed Initiatives with no backup owner',
      },
      over_capacity_period_count: {
        label: 'Over-capacity periods',
        numerator: 'Capacity periods whose demand reaches or exceeds available capacity',
      },

      // --- hours (R3.6, R4.2) ---
      excess_demand_hours: {
        label: 'Excess demand',
        numerator: 'Demand hours above available capacity, summed across over-capacity periods',
      },
      demand_hours_total: {
        label: 'Total demand',
        numerator: 'Demand hours across the Initiatives you can read',
      },

      // --- shares: the Viewer_Scoped set (R1.4, R2.3) ---
      priority_share: {
        label: 'Priority share',
        numerator: 'Initiatives carrying a priority marked as priority-defining',
        denominator: 'Initiatives you can read that match the current selection',
      },
      finality_share: {
        label: 'Finality share',
        numerator: 'Initiatives in a status marked as final',
        denominator: 'Initiatives you can read that match the current selection',
      },
      milestone_readiness_share: {
        label: 'Milestone readiness',
        numerator: 'Required milestones that are completed',
        denominator: 'Required milestones across the Initiatives you can read',
      },
      ff_utilization: {
        label: 'Field force utilisation',
        numerator: 'Demand hours across the capacity periods in scope',
        denominator: 'Available capacity hours across the same periods',
      },

      // --- the tile's own chrome ---
      unit: { count: 'Count', percent: 'Percentage', hours: 'Hours' },
      definition: {
        counts: 'Counts',
        outOf: 'Out of',
        unit: 'Unit',
        excludes: 'Excludes',
        basis: 'Computed over',
        basisCount: '{{count}} readable Initiative(s)',
        readAt: 'Data read at',
        criterion: 'Requirement',
      },
      definitionFor: 'What {{label}} means',
      drillThrough: 'Show the Initiatives behind {{label}}',
      // R1.4: a share is computed over what this reader may see, so two readers can
      // legitimately see different percentages. Stated on the tile, not only in the
      // definition, because it is the caveat a reader needs while reading the number.
      viewerScoped: 'Share of what you can read',
      // R3.11, R1.7: no record matched, which is not the same as a computed zero.
      emptyScope: 'No records in scope',
    },

    /** The dashboard's evaluation coordinates and its filter controls (R3.12, R6.6). */
    scope: {
      planningYear: 'Planning year',
      cutOffDate: 'Cut-off date',
      reset: 'Reset filters',
      // R3.12: the date the figures were evaluated against, when the data was read, and
      // how many readable records it covered. One line, because these three are only
      // meaningful together — a figure without its cut-off date is not reproducible.
      echo: 'Evaluated at {{cutOffDate}} · read {{readAt}} · readiness horizon {{horizon}} days · {{count}} readable initiative(s)',
    },

    /** The seven Governance_Panels (R3.2-R3.7) and their shared states. */
    panel: {
      statusDistribution: 'Status, priority and finality',
      readiness: 'Milestone readiness',
      approvalAndWatchouts: 'Approvals and watchouts',
      dependencies: 'Dependencies',
      capacity: 'Capacity',
      dataQuality: 'Data quality',
      byStatus: 'Initiatives by status',
      bySeverity: 'Open watchouts by severity',
      byRule: 'Initiatives by unmet rule',
      periods: 'Utilisation by period',
      drillThrough: 'Show these initiatives',
      // R3.11, R1.7: the selection matched nothing readable. Deliberately not a row of
      // zeros, which would assert that the records exist and none of them qualified.
      emptyScope:
        'No initiatives match the current filter selection, so there is nothing to report here. Widen the filters to see figures.',
      dataQualityUnavailable: 'The data-quality figures could not be read.',
      withinHorizon: 'Planned within the next {{days}} days',
      noRequiredMilestones: 'Readiness does not apply to these',
      // Says why the figure carries no drill-through: it counts watchouts, and a list of
      // initiatives would report a different number (R3.9).
      watchoutCountNote: 'Counts watchouts; see the roll-up below',
      dependencyCountNote: 'Counts dependencies; see the roll-up below',
      thresholdNote: 'Threshold {{threshold}}% · over capacity at {{overCapacity}}%',
      // R4.2-R4.6: the Management_Dashboard's own panels.
      headline: 'Headline figures',
      yearlyTrend: 'Five-year trend',
      risk: 'Risks',
      initiativeMix: 'Initiative mix',
      manpower: 'Field force demand and capacity',
      // R4.6: stated, and naming the reason. Never zeros (which would say there is no
      // demand) and never empty tiles (which would say the figures could not be computed).
      manpowerOmittedCapability:
        'The utilisation, excess demand and demand-hour figures are not shown because your role does not include access to manpower data.',
      manpowerOmitted: 'The manpower figures are not available for this request.',
      // R5.5: why there is nothing to plot, rather than an empty chart that would read as
      // "there is no demand in any region". The matrix now has a rule, so the remaining
      // absences are the two real ones - no sales cycles defined for the year, or no
      // capacity period covering any of them.
      capacityMatrixUnavailable:
        'Utilisation by region and sales cycle is not available for this selection: it needs sales cycles defined for the planning year and a capacity period covering them.',
    },

    /** R4.3: the five-year trend. */
    trend: {
      caption: 'Initiative count, priority share and finality share by planning year',
      year: 'Planning year',
      current: '(selected)',
      // R4.3: a year holding no readable Initiative is absent, not zero. Plotted as zero
      // it would look like the year the business stopped.
      absent: 'No readable initiatives in this year',
      none: 'No planning years to show.',
    },

    /** The R9 cross-initiative roll-ups. */
    rollup: {
      heading: 'Cross-initiative roll-ups',
      kind: {
        'open-dependencies': 'Open dependencies',
        'unresolved-watchouts': 'Open watchouts',
        'change-history': 'Change history',
        'data-quality-exceptions': 'Data-quality exceptions',
        'ownership-gaps': 'Ownership gaps',
      },
      column: {
        initiative: 'Initiative',
        function: 'Function',
        description: 'Description',
        owner: 'Owner',
        dueDate: 'Due',
        // Open dependencies: the activity date, or the period for a date range.
        activityDate: 'Activity date',
        state: 'State',
        category: 'Category',
        severity: 'Severity',
        eventType: 'Event',
        actor: 'Changed by',
        occurredAt: 'When',
        field: 'Field',
        reason: 'Reason',
        rule: 'Rule',
        tier: 'Tier',
        status: 'Status',
        reasons: 'Reasons',
      },
      reason: {
        owner_inactive: 'Owner is inactive',
        no_backup_owner_on_committed: 'No backup owner on a committed initiative',
      },
      overdue: 'Overdue',
      open: 'Open',
      // R9.8: the complete matching count, which R9.9 requires to equal the panel figure.
      total: '{{count}} matching row(s)',
      page: 'Page {{page}} of {{lastPage}}',
      previous: 'Previous',
      next: 'Next',
      none: 'No rows of this kind across the initiatives in scope.',
      loadError: 'This roll-up could not be read.',
    },

    /** R3.13: the Governance_Metrics export control. */
    export: {
      start: 'Export metrics',
      starting: 'Preparing export…',
      hint: 'Exports every figure on this screen with its definition, filters and cut-off date.',
      queued: 'Export requested',
      // States what did and did not happen. The export pipeline has no download endpoint
      // yet, and claiming a file is ready would be worse than saying so.
      queuedDetail:
        'The export is being produced and has been recorded in the export log (reference {{jobId}}). Downloading completed exports is not available yet.',
      failed: 'The export could not be started.',
    },

    /** R2.5-R2.7, R8.8: the exclusions every Metric_Definition states. */
    exclusion: {
      cancelled: 'Cancelled Initiatives, unless the Cancelled status filter is selected',
      unreadable: 'Initiatives you may not read',
      dataIncomplete: 'Periods whose manpower data is incomplete',
      noRequiredMilestone: 'Initiatives holding no required milestone',
    },

    visual: {
      // R5.10: an absent value is Data_Incomplete, never zero.
      dataIncomplete: 'Data incomplete',
      hoursValue: '{{value}} h',
      noData: 'No data for this selection.',
      // R5.8: no tabular series means no chart at all.
      unavailable: 'This visual is unavailable because its underlying data could not be read.',
      tableCaption: '{{title}} — data table',
      // R5.7's text alternative, derived from the series rather than authored.
      textAlternative: 'Chart of {{measures}} across {{categories}} categories.',
      textAlternativeIncomplete: '{{count}} value(s) are data incomplete and are not plotted.',
      // The measure names of the single-measure visuals (R5.3, R5.4, R5.6). A
      // single-measure chart still needs its column named: the table column header and
      // the legend both read this, and "Value" would say nothing.
      measure: {
        demandHours: 'Demand hours',
        milestones: 'Milestones',
        initiatives: 'Initiatives',
      },
      // R5.2's two marked boundaries. Both labelled on the chart: an unlabelled
      // horizontal rule is decoration, and a reader has to be told which line is which.
      capacityThreshold: 'Threshold {{value}}%',
      overCapacity: 'Over capacity',
      // R5.11's activation, as the accessible name of a Visual_Data_Table header
      // button. The bare label ("Platform 4") would say nothing about what pressing it
      // does, and this is the keyboard route to a narrowing a chart offers only to a
      // pointer (R18.8).
      narrowBy: 'Narrow to {{label}}',
      // The six visuals' own names and category-axis labels (R5.1-R5.6).
      readinessFunnel: 'Required milestones by readiness state',
      readinessState: 'Readiness state',
      workloadHeatmap: 'Demand hours by month and platform',
      month: 'Month',
      utilization: 'Field force utilisation by period',
      period: 'Period',
      topDemandDrivers: 'Initiatives driving the most demand',
      initiative: 'Initiative',
      capacityMatrix: 'Utilisation by region and sales cycle',
      region: 'Region',
      initiativeMixPlatform: 'Initiatives by platform',
      initiativeMixType: 'Initiatives by initiative type',
      platform: 'Platform',
      initiativeType: 'Initiative type',
    },
    crossFilter: {
      // `complan-governance` R6.7: the narrowing is a removable indication.
      remove: 'Remove narrowing {{label}}',
      // R6.8: a narrowing that overrode a dimension the user had set to something
      // else says so, rather than leaving the screen silently disagreeing with the
      // filter bar.
      replacedSuffix: '(replaced {{displaced}})',
      replacedHint: 'This narrowing replaced your selection of {{displaced}}.',
    },
  },

  savedView: {
    open: 'Save view',
    saveCurrent: 'Save current filters',
    namePlaceholder: 'View name',
    heading: 'Saved views',
    none: 'No saved views yet.',
    loadError: 'Could not load saved views.',
    limitReached: 'You have reached the maximum of {{max}} saved views.',
    shared: 'Shared',
    sharedTitle: 'Shared with everyone',
    sharedViewOnly: 'Shared, view only',
    rename: 'Rename',
    share: 'Share',
    unpublish: 'Unpublish',
    delete: 'Delete',
    deactivatedValues: '⚠ Uses deactivated values: {{names}}',
    omittedValues_one: '⚠ {{count}} value no longer exists and was dropped.',
    omittedValues_other: '⚠ {{count}} values no longer exist and were dropped.',
  },

  detail: {
    dialogLabel: 'Initiative detail',
    heading: 'Initiative detail',
    reference: 'Reference {{id}}',
    placeholder:
      'Detail_Panel content renders here (task 23.4). This shell provides the accessible dialog semantics and focus management.',
    // ── Outdated-entry indication (task 22.7, R13.5) ───────────────────────
    // Shown when the panel read a record version later than the calendar bar it
    // opened from; the calendar entry is refreshed from current values in the
    // background while this states that the bar was stale.
    outdated: {
      title: 'Calendar entry was outdated',
      body: 'This initiative changed since the calendar last rendered it. The calendar entry is being refreshed with the current values.',
    },
    // ── Detail_Panel content (task 23.4, R12) ─────────────────────────────
    // Additive block: the drawer's header, tab set, per-tab field labels,
    // markers and list controls. Keep it inside `detail.*`; do not reorder the
    // neighbouring blocks.
    close: 'Close',
    loading: 'Loading initiative…',
    notFound: 'This initiative could not be found.',
    tab: {
      overview: 'Overview',
      workload: 'Workload',
      milestones: 'Milestones',
      dependencies: 'Dependencies',
      watchouts: 'Watchouts',
      history: 'History',
      inputRequests: 'Input requests',
    },
    field: {
      title: 'Title',
      details: 'Details',
      owner: 'Owner',
      backupOwner: 'Backup owner',
      timing: 'Timing',
      planningYear: 'Planning year',
      status: 'Status',
      priority: 'Priority',
      confidential: 'Confidential',
      primaryPlatform: 'Primary platform',
      additionalPlatforms: 'Additional platforms',
      categories: 'Categories',
      initiativeTypes: 'Initiative types',
      cscAttributes: 'CSC attributes',
      salesCycles: 'Sales cycles',
      audiences: 'Audiences',
      channels: 'Channels',
      customerGroups: 'Customer groups',
      salesCycleSource: 'Sales cycle source',
      involvement: 'Involvement',
      version: 'Version',
      created: 'Created',
      modified: 'Last modified',
      cancellationReason: 'Cancellation reason',
    },
    value: {
      yes: 'Yes',
      no: 'No',
      none: '—',
      inactiveAccount: '(inactive)',
      byAt: '{{actor}} · {{at}}',
    },
    involvement: {
      fieldForce: 'Field Force',
      dte: 'DTE',
      csc: 'CSC',
      none: 'None',
    },
    salesCycle: {
      source: {
        derived: 'Derived',
        manual: 'Manual override',
      },
    },
    // ── Manual Sales_Cycle override marker (R7.6) ─────────────────────────
    override: {
      title: 'Manual sales cycle override',
      reason: 'Reason',
      actor: 'Overridden by',
      at: 'Overridden at',
      manualSet: 'Manually assigned',
      derivedSet: 'Currently derived',
    },
    // ── Changed-after-approval flag (R12.10) ──────────────────────────────
    changedAfterApproval: {
      title: 'Changed after approval',
      body: 'This initiative was materially changed after it was approved.',
      actor: 'Last changed by',
      at: 'Last changed at',
    },
    milestones: {
      empty: 'No milestones recorded.',
      planned: 'Planned {{date}}',
      completed: 'Completed {{date}}',
      notCompleted: 'Not completed',
      colType: 'Milestone',
      colPlanned: 'Planned',
      colCompleted: 'Completed',
      colState: 'State',
      // R8.10: a milestone whose type carries no mandatory rule flag. Shown, and outside
      // every readiness figure. The five readiness states themselves live in the shared
      // `readinessState` block, so the panel and the dashboards read one vocabulary
      // (R8.6).
      notRequired: 'Not required',
    },
    // ── Workload tab: the Field Force calculation chain (Manpower R5.8) ────
    workload: {
      heading: 'Calculation chain',
      hint: 'Every factor, its source and its version.',
      loading: 'Calculating workload…',
      loadError: 'The workload could not be calculated.',
      noInvolvement:
        'No Field Force involvement recorded for this initiative — it contributes no hours to capacity views.',
      noAccess: 'You do not have access to Field Force workload figures.',
      reconciliationFailed:
        'The reconciliation control did not hold for this calculation. Treat these figures as unverified and report them.',
      dataIncompleteSummary:
        '{{count}} driver(s) have no applicable effort assumption, so their hours are not included.',
      componentTitle: '{{role}} component',
      componentTitleWithActivity: '{{role}} \u00b7 {{activity}}',
      overrideBadge: 'Population override',
      dataIncomplete: 'Data incomplete',
      activityType: 'Activity type',
      audience: 'Audience',
      region: 'Region',
      populationUnit: 'Population unit',
      posSegments: 'POS segments selected',
      derivedPopulation: 'Derived population count',
      derivedPopulationSourced: 'Derived population count (sourced {{at}})',
      population: 'Impacted population',
      populationOverride: 'Manual override — {{reason}}',
      coverageFactor: 'Coverage factor',
      frequency: 'Frequency',
      effortPerUnit: 'Effort per unit',
      effortPerUnitVersioned: 'Effort per unit (assumption v{{version}})',
      fixedOverhead: 'Fixed overhead',
      activityDates: 'Activity dates',
      driverDemand: 'Driver demand',
      platformAllocation: 'Platform allocation',
      periodAllocation: 'Period allocation',
      periodAllocationNote: 'as allocated by the demand engine',
      totalReconciles: 'Total {{year}} — reconciles to initiative demand',
      hoursValue: '{{value}} h',
      minutesValue: '{{value}} min',
      percentValue: '{{value}}%',
    },
    dependencies: {
      empty: 'No dependencies recorded.',
      owner: 'Owner',
      due: 'Activity date',
      open: 'Open',
      closed: 'Closed',
      colArea: 'Area',
      colDependency: 'Dependency',
      colOwner: 'Owner',
      colDue: 'Activity date',
      colState: 'State',
      function: {
        dte: 'DTE',
        csc: 'CSC',
      },
    },
    referenceLinks: {
      title: 'Reference links',
      empty: 'No reference links.',
    },
    history: {
      empty: 'No change history you may view.',
      inputRequestsEmpty: 'No input requests recorded.',
      loadMore: 'Load 25 more',
      changedField: '{{object}} · {{field}}',
    },
    action: {
      edit: 'Edit',
      duplicate: 'Duplicate',
      cancel: 'Cancel initiative',
      delete: 'Delete',
      inputRequest: 'Request input',
      // The lifecycle move buttons (R9.12); `status` is the resolved target
      // label, e.g. "Submit for review", "Move to Planned", "Approve".
      transitionTo: 'Move to {{status}}',
    },
    // ── Request input dialog (R2.6, R2.11) ────────────────────────────────
    inputRequestDialog: {
      title: 'Request input',
      body: 'Submit a comment against this initiative. This does not change any initiative field.',
      textLabel: 'Your message',
      textHint: '1 to 2,000 characters.',
      cancel: 'Cancel',
      submit: 'Submit',
      submitting: 'Submitting…',
      error: 'This input request could not be submitted. Please try again.',
    },
    // ── Cancel initiative dialog (R9.6) ────────────────────────────────────
    cancelDialog: {
      title: 'Cancel this initiative?',
      body: 'Cancelling sets the status to Cancelled. All field values, associations, milestones, dependencies and history are retained unchanged.',
      reasonLabel: 'Cancellation reason',
      reasonHint: '1 to 500 characters.',
      cancel: 'Keep initiative',
      confirm: 'Cancel initiative',
      confirming: 'Cancelling…',
      error: 'This initiative could not be cancelled. Please try again.',
    },
    // ── Status transition dialog (R9.2, R9.12, R9.13) ──────────────────────
    transitionDialog: {
      title: 'Move to {{status}}?',
      body: 'This changes the initiative status to {{status}}. The move and its reason are recorded in the change history.',
      reasonLabel: 'Reason',
      reasonHint: '1 to 500 characters. Required for this transition.',
      commentLabel: 'Comment (optional)',
      cancel: 'Cancel',
      confirm: 'Move to {{status}}',
      confirming: 'Applying…',
      error:
        'This transition could not be applied. It may require approver rights, or the initiative may have changed since you opened it. Please reopen and try again.',
    },
    // ── Delete confirmation dialog (R9.9) ──────────────────────────────────
    deleteDialog: {
      title: 'Delete this initiative?',
      body: 'The initiative is removed from every view. It can be restored by a System Administrator within the retention period.',
      cancel: 'Keep initiative',
      confirm: 'Delete initiative',
      confirming: 'Deleting…',
      error: 'This initiative could not be deleted. Please try again.',
    },
    // ── Duplicate dialog (R9.8) ────────────────────────────────────────────
    duplicateDialog: {
      title: 'Duplicate this initiative?',
      body: 'Choose which field groups to copy into the new initiative. The new record holds status Draft, an empty approval history and a cleared changed-after-approval flag; the confidentiality attribute is copied from this initiative.',
      fieldGroup: {
        basic_information_and_ownership: 'Basic information and ownership',
        timing_and_sales_cycle: 'Timing and sales cycle',
        milestones: 'Milestones',
        classification: 'Classification',
        audience_and_channels: 'Audience and channels',
        field_force: 'Field Force',
        dte_csc_dependencies: 'DTE / CSC dependencies',
        governance_attributes: 'Governance attributes',
      },
      selectAll: 'Select all',
      cancel: 'Cancel',
      confirm: 'Duplicate',
      confirming: 'Duplicating…',
      error: 'This initiative could not be duplicated. Please try again.',
      noneSelected: 'Select at least one field group to copy.',
    },
  },

  // ── Initiative list screen ───────────────────────────────────────────────
  initiativeList: {
    // The Bulk_Edit selection column's accessible names (R10.4, R18.8): a bare
    // checkbox tells a screen reader nothing about what it selects.
    selectAllOnPage: 'Select every initiative on this page',
    selectRow: 'Select {{ref}}',
    tableCaption: 'Initiatives, sorted by identifier',
    filterBarLabel: 'Initiative filters',
    colId: 'ID',
    colTitle: 'Title',
    colPlatform: 'Platform',
    colSalesCycle: 'Sales Cycle',
    colFieldForceRole: 'FF role',
    colStatus: 'Status',
    primaryPlatform: '{{name}} (primary)',
    noSalesCycle: 'None',
    noFieldForce: 'Not involved',
    filterStatus: 'Status',
    filterPlatform: 'Platform',
    filterSearch: 'Search',
    searchPlaceholder: 'ID, title, owner, platform…',
    allStatuses: 'All statuses',
    allPlatforms: 'All platforms',
    inactiveOption: '{{name}} (inactive)',
    pageSize: 'Per page',
    empty: 'No initiative matches these filters.',
    range: 'Showing {{first}}–{{last}} of {{total}}',
    resultCount_one: '{{count}} initiative visible',
    resultCount_other: '{{count}} initiatives visible',
    previousPage: 'Previous',
    nextPage: 'Next',
    loadError: {
      title: 'The list could not be loaded',
      description: 'The request failed. Adjust the filters or try again.',
    },
    labelsUnavailable: {
      title: 'Some labels could not be loaded',
      description:
        'Platform and Sales Cycle names are unavailable, so those columns cannot be shown.',
    },
    // Shown in place of a display name the reference read could not resolve. An
    // em dash rather than the raw identifier: a UUID in a name column reads as
    // corrupt data (see `labelLookup` in `initiative-list-data.ts`).
    unresolvedLabel: '—',
    unresolvedLabelTitle: 'Name unavailable (identifier {{id}})',
  },

  // ── Initiative Intake wizard (task 23.1) ─────────────────────────────────
  // Scoped block; add intake keys here without reordering neighbouring blocks so
  // the concurrent Sales_Cycle form agent (23.3) can add its own block cleanly.
  intake: {
    title: 'Initiative Intake',
    subtitle: 'New initiative',
    editTitle: 'Edit initiative',
    requiredMark: '*',
    inactiveMarker: '(inactive)',
    // The "select all" toggle above a checkbox group (Channels); ticks every shown option.
    selectAll: 'Select all',
    submitError: {
      title: 'The initiative could not be saved',
      description:
        'The save was refused and nothing was stored. Check your entries and try again; if it keeps failing, quote the correlation id from the browser console when reporting it.',
    },
    optionsError: {
      title: 'Selectable values could not be loaded',
      description:
        'The owner list and the configured value sets are unavailable, so the form cannot be filled in. Reload the page to try again.',
    },
    autosave: {
      idle: 'All changes captured locally',
      saving: 'Saving…',
      saved: 'Saved',
    },
    nav: {
      back: '← Back',
      continue: 'Continue →',
      // The shortcut to step 9 from any other step. Editing an existing Initiative
      // is usually "change one field, then save", and Save draft / Submit live on
      // the Validation step (UI spec §6.3) — so without this the user walks every
      // step or aims at the stepper tab. The count variant reports how many
      // required inputs are still empty, so the destination is predictable.
      skipToValidation: 'Skip to validation',
      skipToValidationWithMissing_one: 'Skip to validation · {{count}} input missing',
      skipToValidationWithMissing_other: 'Skip to validation · {{count}} inputs missing',
      skipToValidationHint: 'Go to the validation summary to save or submit this initiative',
      submit: 'Save initiative',
      stepper: 'Intake steps',
      completed: '✓',
      // The stepper's incomplete marker, beside `completed` because the two are one
      // vocabulary. `!` is the governance glyph of UI spec §3.8: a step owing a
      // required input blocks submission just as a governance finding does.
      hasMissing: '!',
      // The same fact in words, as each tab's accessible name and tooltip, so the
      // glyph above never carries it alone (R18.8).
      stepMissing_one: '{{step}}, {{count}} required input still missing',
      stepMissing_other: '{{step}}, {{count}} required inputs still missing',
    },
    step: {
      basics: 'Basics',
      timing: 'Timing',
      classification: 'Classification',
      audience: 'Audience',
      fieldForce: 'Field Force',
      milestones: 'Milestones',
      dependencies: 'Dependencies',
      governance: 'Governance',
      validation: 'Validation',
    },
    basics: {
      heading: 'Basic information and ownership',
      titleLabel: 'Title',
      titlePlaceholder: 'Initiative title',
      detailsLabel: 'Details',
      detailsPlaceholder: 'Description and context',
      ownerLabel: 'Owner',
      ownerPlaceholder: 'Select an owner',
      backupOwnerLabel: 'Backup owner',
      backupOwnerPlaceholder: 'Select a backup owner (optional)',
    },
    timing: {
      heading: 'Timing and Sales Cycle',
      startDateLabel: 'Start date',
      endDateLabel: 'End date',
      // The accessible name for the `DateRangeField` trigger button.
      dateRangeLabel: 'Start and end date',
      salesCycleNote: 'Enter both dates to see the proposed Sales Cycle assignment.',
      cyclePickerLabel: 'Select Sales Cycles',
      cyclePickerHint:
        'Selecting one or more Sales Cycles fills the start and end date from the earliest and latest selected cycle.',
      cycleOptionLabel: '{{name}} ({{year}}) · {{range}}',
      cyclesLoading: 'Loading Sales Cycles…',
      cyclesLoadError: 'The Sales Cycles could not be loaded. Enter the dates directly instead.',
      cyclesEmpty: 'No active Sales Cycle is maintained yet.',
      cyclesAddedByRange:
        'The resulting date range also covers {{cycles}}, so these are included in the assignment.',
      derivingNote: 'Deriving the Sales Cycle assignment…',
      derivedSalesCycleNote: 'Proposed Sales Cycle assignment: {{cycles}}.',
      noSalesCycleWarning:
        'No active Sales Cycle covers these dates. Submitting for review will be blocked until the dates fall within an active Sales Cycle.',
      manualOverrideNote:
        'This Initiative carries a manual Sales Cycle override. Changing the dates does not replace it.',
    },
    classification: {
      heading: 'Classification',
      platformLegend: 'Platform',
      platformNote: 'Select the platform this initiative belongs to.',
      categoryLegend: 'Categories',
      initiativeTypeLegend: 'Initiative types',
      priorityLabel: 'Priority',
      priorityPlaceholder: 'Select a priority',
    },
    audience: {
      heading: 'Audience and impacted channels',
      channelLegend: 'Channels',
      audienceLegend: 'Audiences',
      namedCustomerHeading: 'Named customers / key accounts',
      namedCustomerNote: 'Required for the KAM audience.',
      partnerGroupHeading: 'Partner groups / POS segments',
      partnerGroupNote: 'Required for the IND audience.',
      customerGroupLegend: 'Customer / partner groups',
    },
    fieldForce: {
      heading: 'Field Force involvement',
      involvementLabel: 'Field Force involved?',
      roleLegend: 'Field Force roles',
      roleAde: 'ADE',
      roleBde: 'BDE',
      roleBlockHeading: '{{role}} driver block',
      // Several activities per role (one demand driver each).
      activityHeading: '{{role}} activity {{index}}',
      activityHeadingWithType: '{{role}} activity {{index}} \u00b7 {{activity}}',
      addActivity: '+ Add {{role}} activity',
      addTypesToggle: 'Add activity types\u2026',
      addTypesHint:
        'Creates one activity per selected type. Region, population unit, store types, POS count, coverage, frequency and dates are copied from the last {{role}} activity; each type uses its own minutes per unit from the Effort Matrix.',
      addTypesButton_one: 'Add {{count}} activity',
      addTypesButton_other: 'Add {{count}} activities',
      removeActivity: 'Remove',
      removeActivityLabel: 'Remove {{name}}',
      allTypesUsed: 'Every activity type is already used for {{role}}.',
      activityLimit: 'An initiative can have at most {{max}} Field Force activities.',
      activityTypeLabel: 'Activity type',
      activityTypePlaceholder: 'Select an activity type',
      storeTypeLegend: 'Store types / segments',
      derivedPosLabel: 'Derived POS count',
      derivedPosSource: 'Summed from selected store types',
      overrideLabel: 'Manual POS override',
      overridePlaceholder: 'Number of points of sale',
      overrideBadge: 'Override',
      overrideReasonLabel: 'Override reason',
      overrideReasonPlaceholder: 'Why the manual count replaces the derived one',
      overrideWarning:
        '⚠ This count differs from the derived {{derived}} and is recorded as an override, which requires a reason.',
      overrideReasonTooShort: 'At least {{min}} characters are required.',
      noStoreTypes:
        'No POS segments are configured. Add them in Administration → Master data to derive a POS count automatically, or enter the count directly below.',
      // ── Manpower demand inputs (R4.1-R4.6) ──────────────────────────────
      // Present on every Field Force scope entry. All optional: an entry kept for
      // governance tracking alone is legitimate (R5.2). Together, though, the
      // population unit, count, coverage factor and frequency are what make the
      // entry a demand driver, so the hints say so rather than leaving a planner
      // wondering why nothing reaches the capacity screens.
      regionLabel: 'Region',
      regionPlaceholder: 'Select a region',
      regionHint: 'Capacity and utilisation are reported per region; an entry with no region reaches no region\u2019s figures.',
      populationUnitLabel: 'Population unit',
      populationUnitPlaceholder: 'Select a population unit',
      populationUnitHint: 'What the impacted count is counted in.',
      impactedPopulationLabel: 'Impacted population count',
      impactedPopulationHint:
        'Filled from the selected POS segments when there are any; enter a different number to override it.',
      coverageFactorLabel: 'Coverage factor (%)',
      coverageFactorHint: '0 to 100. Zero means the activity is out of scope this cycle and only fixed overhead applies.',
      frequencyLabel: 'Frequency',
      frequencyHint: 'How many times the activity is performed per point of sale.',
      activityStartLabel: 'Activity start',
      activityEndLabel: 'Activity end',
      // The accessible name for the `DateRangeField` trigger button.
      activityDateRangeLabel: 'Activity start and end date',
      activityDatesHint:
        'Pre-filled from the initiative\u2019s timing. Narrow it to a window inside those dates, or clear both.',
      driverIncompleteNote:
        'Population unit, impacted count, coverage factor and frequency are all needed before this entry produces demand hours.',
      hasMaterialLabel: 'Field Force material?',
      workOrderLabel: 'Work order',
      workOrderPlaceholder: 'Link or text',
      informationContributionLabel: 'Information contribution',
      informationContributionPlaceholder: 'Link or text',
      addRole: 'Add role entry',
      calcPreviewNote:
        'The derived-hours calculation and capacity-impact preview are part of the manpower engine and are not shown here.',
    },
    milestones: {
      heading: 'Milestones',
      typeLabel: 'Milestone type',
      typePlaceholder: 'Select a milestone type',
      plannedDateLabel: 'Planned date',
      completedDateLabel: 'Completed date',
      add: 'Add milestone',
      remove: 'Remove milestone',
      none: 'No milestones added yet.',
    },
    dependencies: {
      heading: 'DTE and CSC dependencies',
      dteInvolvementLabel: 'DTE involved?',
      cscInvolvementLabel: 'CSC involved?',
      dteBlockHeading: 'DTE dependency',
      cscBlockHeading: 'CSC dependency',
      cscAttributeLegend: 'CSC attributes',
      descriptionLabel: 'Dependency description',
      descriptionPlaceholder: 'What is needed from this function',
      ownerLabel: 'Dependency owner',
      ownerPlaceholder: 'Responsible person',
      // Activity date: a single date or a period (migration 33).
      dueDateLabel: 'Activity date',
      dateModeLabel: 'Activity date type',
      singleDate: 'Single date',
      period: 'Period',
      periodLabel: 'Activity period start and end date',
    },
    governance: {
      heading: 'Governance attributes',
      confidentialLabel: 'Confidential initiative?',
      confidentialReasonLabel: 'Confidentiality reason',
      confidentialReasonPlaceholder: 'Reason for the confidentiality change',
    },
    validationStep: {
      heading: 'Validation summary',
      seamNote:
        'The four-tier validation summary and conflict dialog land in task 23.2. This step reserves its place in the wizard.',
      // The missing-required-inputs advisory on Step 9. A rule cannot fail on a
      // value that is not there yet, so an incomplete intake would otherwise
      // reach this step with an empty summary; these name what is still empty and
      // where to fill it in. Field names resolve through `validationSummary.field.*`
      // and step names through `intake.step.*`, so nothing is duplicated here.
      missingHeading: 'Required inputs are missing',
      missingBody_one:
        'One required input is still empty. Fill it in before submitting this initiative for review.',
      missingBody_other:
        '{{count}} required inputs are still empty. Fill them in before submitting this initiative for review.',
      goToStep: 'Go to {{step}}',
    },
    discard: {
      title: 'Discard {{section}} details?',
      body: 'Turning this off will discard the following on the next save:',
      confirm: 'Discard on save',
      cancel: 'Keep values',
      section: {
        fieldForce: 'Field Force',
        dte: 'DTE',
        csc: 'CSC',
      },
      // Keyed by a dot-free slug of each logical field path (the domain map keys
      // contain '.', which is i18next's key separator, so they are slugged with
      // '_' before lookup — see DISCARD_FIELD_LABEL_KEY in the wizard).
      field: {
        fieldForceScope_roles: 'Field Force roles',
        fieldForceScope_activityTypes: 'activity types',
        fieldForceScope_audiences: 'audiences',
        fieldForceScope_hasMaterial: 'Field Force material',
        fieldForceScope_workOrder: 'work order',
        fieldForceScope_informationContribution: 'information contribution',
        dte_dependencyFunction: 'DTE dependency',
        dte_dependencyDescription: 'DTE dependency description',
        dte_ownerUserId: 'DTE dependency owner',
        dte_dueDate: 'DTE activity date',
        csc_dependencyFunction: 'CSC dependency',
        csc_dependencyDescription: 'CSC dependency description',
        csc_ownerUserId: 'CSC dependency owner',
        csc_dueDate: 'CSC activity date',
        csc_attributeIds: 'CSC attributes',
      },
    },
    unsaved: {
      title: 'Unsaved changes',
      body: 'You have unsaved changes. Save, discard, or continue editing?',
      save: 'Save',
      discard: 'Discard',
      continue: 'Continue editing',
    },
    sessionEnded: {
      message: 'Your previous session ended before your changes were saved, so they were not kept.',
      dismiss: 'Dismiss',
    },
  },

  // ── Manpower screens (tasks 14.1-14.4, R5-R9) ─────────────────────────────
  // ── Manpower screens (tasks 13.1-13.2, 14.1-14.4; R2-R7) ─────────────────
  // Scoped block: the four Manpower screens and the vocabulary they share.
  // Demand/Capacity terminology follows the requirements' spelling of the
  // domain terms (Demand_Hours, Capacity_Hours, Utilization, Bottleneck_State).
  manpower: {
    // Shared units and markers, so an hours figure or a version marker reads the
    // same on all four screens.
    hours: '{{value}}h',
    // Effort per unit is the one Manpower quantity measured in minutes rather than
    // hours, and it sits next to an hours column in the Effort Matrix — so the unit
    // travels with the figure or the two read as the same quantity.
    minutes: '{{value}} min',
    percent: '{{value}}%',
    version: 'v{{version}}',
    /** Stands in for a figure that does not exist, as distinct from zero. */
    absent: '—',
    bottleneckState: {
      normal: 'Normal',
      warning: 'Warning',
      overCapacity: 'Over capacity',
      dataIncomplete: 'Data incomplete',
    },
    // The calculation chain of R5.8, disclosed on every Manpower screen.
    formula: {
      heading: 'How these figures are calculated',
      demandLabel: 'Demand hours',
      demand:
        '= impacted POS × coverage × frequency × effort per unit ÷ 60, plus fixed overhead.',
      capacityLabel: 'Capacity hours',
      capacity:
        '= headcount × (workdays − out-of-office days) × productive hours per workday × availability factor.',
      utilisationLabel: 'Utilisation',
      utilisation: '= demand hours ÷ capacity hours × 100.',
      allocationLabel: 'Period allocation:',
      assumptionSetLabel: 'Assumption set:',
      assumptionSetWithDate: '{{version}} (effective {{date}})',
    },
    capacityUtilisation: {
      title: 'Capacity & Utilisation',
      subtitle: 'Field Force demand, capacity and utilisation by period',
      noAccess: 'You do not have access to manpower data.',
      // The accessible name of the filter strip (UI spec §3.6) and of the
      // reconciliation table, both landmarks a screen reader lists by name.
      filterBarLabel: 'Capacity and utilisation filters',
      tableCaption: 'Demand, capacity and utilisation per period',
      planningYear: 'Planning year',
      filterRole: 'FF role',
      filterRegion: 'Region',
      // Both filters offer an aggregate: the screen opens on the whole field force
      // and narrowing to one scope is the reader's next step, not a prerequisite.
      allRoles: 'All roles',
      allRegions: 'All regions',
      // The calculation is scoped to one role and one Region, so a deployment
      // with no Region master data has nothing to calculate against.
      noRegions: 'No regions',
      noRegionsTitle: 'No regions configured',
      noRegionsBody:
        'Capacity and utilisation are calculated per Field Force role and region. Add at least one region in Administration → Master data before using this screen.',
      totalDemand: 'Demand hours',
      totalCapacity: 'Capacity hours',
      utilisationKpi: 'Utilisation',
      avgUtilization: 'Average utilisation',
      bottlenecks: 'Periods in breach',
      // ── The supporting line under each headline figure ────────────────────
      kpiInitiatives_one: '{{count}} initiative',
      kpiInitiatives_other: '{{count}} initiatives',
      // The two rate assumptions of the capacity formula, so the total is not an
      // unexplained number. "Mixed" when the scopes in view disagree — printing one
      // scope's figures as though they explained the whole total would be a
      // plausible-looking lie.
      kpiAssumptions: '{{hours}} h/day × availability {{availability}}',
      kpiAssumptionsMixed: 'Mixed assumptions across scopes',
      kpiThreshold: 'threshold {{threshold}}%',
      kpiExcessHours: '{{hours}} excess hours',
      noRoleDemand: 'No demand recorded',
      roleShare: '{{role}} {{percent}}%',
      scopeAllRegions_one: '{{count}} region',
      scopeAllRegions_other: 'All {{count}} regions',
      scopeOneRegion: 'Selected region',
      // The info affordance on each KPI: what the figure is made of, since a
      // governed number that cannot be explained cannot be challenged.
      infoAria: 'How {{label}} is calculated',
      infoDemand:
        'Demand hours are the sum of each Field Force demand driver in scope: population × coverage × frequency × effort per unit, plus any fixed overhead. Drivers with no resolvable effort assumption are excluded rather than counted as zero.',
      infoCapacity:
        'Capacity hours are headcount × (workdays − out-of-office days) × productive hours per day × availability factor, summed across every capacity period in scope. A period with no agreed headcount is excluded rather than treated as zero.',
      infoUtilisation:
        'Utilisation is total demand hours divided by total capacity hours. A period is flagged as a warning at or above the configured threshold, and as over capacity above 100%.',
      infoBreach:
        'Periods at or above the threshold, including those over 100%. The excess hours are the demand that has no capacity behind it, summed over the over-capacity periods only.',
      // The hours of planned work with no capacity behind them, summed over the
      // over-capacity periods only. Spare hours in one period do not staff an
      // overloaded other one, so this is never netted against them.
      shortfall: 'Capacity shortfall',
      overCapacityTitle: 'Planned demand exceeds capacity',
      overCapacityBody_one:
        '{{count}} period is over capacity, by {{hours}} hours in total. Reduce planned activity in that period, raise its capacity, or accept the overload as recorded.',
      overCapacityBody_other:
        '{{count}} periods are over capacity, by {{hours}} hours in total. Reduce planned activity in those periods, raise their capacity, or accept the overload as recorded.',
      gridHeading: 'Utilisation by period',
      // ── The role-by-period utilisation matrix (R6.2-R6.4, R7.1) ───────────
      matrixHeading: 'Utilisation by period — {{roles}}',
      matrixSubtitle:
        'Demand ÷ capacity, per role. Amber from {{threshold}}%, red above 100%. Click a cell to drill through to the contributing initiatives.',
      matrixCaption: 'Utilisation per Field Force role and period',
      noPoolForPeriod: 'No capacity period is recorded for this role in this period.',
      reconciliationHeading: 'Reconciliation',
      // ── The Bottleneck_State key under the utilisation matrix (R6.4) ───────
      // Each names the state *and* the range that defines it. The warning threshold
      // is interpolated because it is configurable (R6.6) — a legend that hardcoded
      // 85 would misdescribe how the colours were chosen the day it changed.
      legend: {
        normal: 'Normal <{{threshold}}%',
        warning: 'Warning {{threshold}}–100%',
        over_capacity: 'Over capacity >100%',
        data_incomplete: 'Data incomplete — excluded from totals',
      },
      legendHint: 'Click any cell to drill through to the contributing initiatives.',
      period: 'Period',
      demand: 'Demand h',
      capacity: 'Capacity h',
      utilization: 'Utilisation',
      excessOrFree: 'Excess / remaining',
      excessOver: '+{{value}}',
      excessFree: '{{value}} free',
      excessExact: 'Exactly matched',
      topDriver: 'Top driver',
      topDriverTitle: '{{ref}} contributes {{hours}}h of this period, of {{count}} initiatives.',
      // Appended to a capacity figure that omits a scope with no agreed headcount,
      // so a partial total never passes as a complete one (R3.4).
      partialCapacity_one: '({{count}} scope incomplete)',
      partialCapacity_other: '({{count}} scopes incomplete)',
      // The strip's column label is compact (no year), so the hover text names the
      // period in full — otherwise a range's year is stated nowhere.
      cellTooltip_one: '{{period}} — {{demand}}h of demand from {{count}} initiative',
      cellTooltip_other: '{{period}} — {{demand}}h of demand from {{count}} initiatives',
      // ── The drill-through panel (R6.5) ────────────────────────────────────
      drillThrough: {
        title: 'Contributing initiatives',
        tableCaption: 'Initiatives contributing demand hours to this period',
        description_one:
          '{{count}} initiative accounts for this period’s {{hours}} demand hours. Select one to open it.',
        description_other:
          '{{count}} initiatives account for this period’s {{hours}} demand hours. Select one to open it.',
        empty:
          'No initiative allocates demand into this period, so its capacity is entirely unused.',
        colInitiative: 'Initiative',
        colDrivers: 'Drivers',
        colHours: 'Demand h',
      },
      // A Capacity_Period whose effective range is not a whole month or year keeps
      // its literal dates: there is no shorter honest name for an arbitrary range.
      periodRange: '{{start}} – {{end}}',
      status: 'Bottleneck state',
      allocationMethod:
        'Demand is allocated across periods in proportion to each period’s share of the driver’s active window.',
      noData: 'No capacity periods match this scope, so there is nothing to report yet.',
      loadError: 'The capacity and utilisation calculation could not be loaded.',
    },
    demandExplorer: {
      title: 'Demand Explorer',
      subtitle: 'Demand hours by initiative, Field Force role, activity type and region',
      noAccess: 'You do not have access to manpower data.',
      filterBarLabel: 'Demand explorer filters',
      tableCaption: 'Demand hours by group',
      // The accessible name of the data-incomplete count pill: the numeral alone
      // does not say what it counts (R18.8).
      dataIncompleteCount: '{{count}} drivers with incomplete data',
      planningYear: 'Planning year',
      groupBy: 'Group by',
      byInitiative: 'Initiative',
      byRole: 'FF role',
      byActivityType: 'Activity type',
      byRegion: 'Region',
      granularity: 'Granularity',
      monthly: 'Month',
      weekly: 'Calendar week',
      filterRole: 'FF role',
      filterRegion: 'Region',
      allRoles: 'All roles',
      allRegions: 'All regions',
      // The grouped column's heading changes with the grouping dimension.
      column: {
        initiative: 'Initiative',
        'ff-role': 'FF role',
        'activity-type': 'Activity type',
        region: 'Region',
      },
      initiatives: 'Initiatives',
      drivers: 'Drivers',
      demandHours: 'Demand h',
      share: 'Share',
      dataIncomplete: 'Incomplete',
      total: 'Total',
      // "ADE + BDE" rather than "All roles": the two roles are the whole of the
      // field force, so naming them is more informative than saying "all".
      allRolesCombined: 'ADE + BDE',
      // The accessible name of the per-row incomplete pill; a bare numeral does not
      // say what it counts (R18.8).
      dataIncompleteAria_one: '{{count}} driver excluded for incomplete data',
      dataIncompleteAria_other: '{{count}} drivers excluded for incomplete data',
      // ── The annual workload heatmap (R5.3, R5.4) ──────────────────────────
      heatmapHeading: 'Annual workload heatmap',
      heatmapSubtitle: 'Demand hours per period and Platform.',
      heatmapCaption: 'Demand hours per Platform and period',
      heatmapPlatformColumn: 'Platform',
      heatmap: 'Workload across periods — {{group}}',
      heatmapCellTooltip: '{{platform}}, {{period}}: {{hours}}h',
      heatmapLegend:
        'Shading is relative to the busiest cell in the matrix. Figures are printed on the heaviest cells; hover any cell for its exact hours.',
      // ── The Reconciliation_Controls of R5.7, named individually ────────────
      reconciliationControls: 'Reconciliation controls:',
      control: {
        initiativeDrivers: 'component sum = initiative demand',
        driverPeriods: 'period sum = driver demand',
        roleTotals: 'ADE + BDE = role total',
      },
      controlBroken: 'did not hold',
      noActivityTypesTitle: 'No activity types configured',
      noActivityTypes:
        'No Field Force activity types are configured, so grouping by activity type will show identifiers only.',
      allocationMethod:
        'Even distribution across the initiative window unless explicit activity dates are set.',
      reconciliationTitle: 'Reconciliation control did not hold',
      reconciliationBody:
        'The sum of the per-driver demand hours does not match the reported total. The figures below are shown as calculated, and the discrepancy has been logged for investigation.',
      noData: 'No demand drivers match the current filters.',
      loadError: 'The demand calculation could not be loaded.',
    },
    capacityInputs: {
      title: 'Capacity Inputs',
      subtitle: 'Capacity periods by Field Force role and region',
      noAccess: 'You do not have access to capacity inputs.',
      filterBarLabel: 'Capacity period filters',
      tableCaption: 'Capacity periods by Field Force role, region and period',
      filterRole: 'FF role',
      filterRegion: 'Region',
      allRoles: 'All roles',
      allRegions: 'All regions',
      showAllVersions: 'Show all versions',
      superseded: '(superseded)',
      deactivated: '(deactivated)',
      add: 'Add capacity period',
      // The scope of a capacity period is a role and a region, so there is
      // nothing to record against until at least one region exists.
      needsRegion: 'Add a region in Administration → Master data first.',
      registerHeading: 'Capacity periods',
      registerSubtitle:
        'Headcount, working days and OOF by role and region. Editable by Commercial Planning and Field Force Leadership.',
      availableDays: 'Available days',
      // "Override" revises a stated figure; "Resolve" supplies a missing one. Same
      // action, different reason, and the register should say which.
      override: 'Override',
      resolve: 'Resolve',
      // Shown when a period carries its own productive-hours or availability rate
      // rather than the assumption defaults, so a capacity figure the assumptions
      // card cannot explain still says where it came from.
      ownRatesMarker: '†',
      ownRates: 'This period uses {{hours}} h/day and {{availability}} availability.',
      // ── The planning assumptions panel (R3.2, R6.4, A13) ───────────────────
      assumptions: {
        heading: 'Planning assumptions',
        subtitle: 'Versioned; changes are audited and trigger recalculation.',
        loadError: 'The planning assumptions could not be loaded.',
        productiveHours: 'Productive hours per available workday',
        productiveHoursHint:
          'Sales-Cycle-dedicated hours per available workday, applied to a new capacity period.',
        availability: 'Availability factor',
        availabilityHint:
          'Adjustment for partial allocation or known operational constraints, applied to a new capacity period.',
        allocationMethod: 'Default allocation method',
        allocationEven: 'Even distribution across window',
        allocationMethodHint:
          'Demand is spread evenly across an initiative’s window unless explicit activity dates are set.',
        thresholds: 'Warning / over-capacity threshold',
        thresholdPair: '{{warning}}% / {{over}}%',
        thresholdsHint:
          'A period is flagged as a warning at or above the first figure. The second is definitional: above 100% the demand exceeds the capacity available.',
        recalculationNotice:
          'Changing an assumption recalculates all baseline records from the effective date. Historical versions remain traceable.',
        readOnlyNote:
          'Shown as calculated with. Editing an assumption is a governed change that is not yet available from this screen.',
      },
      // ── The capacity formula, worked through on real figures (R3.5, R6.2) ──
      formula: {
        heading: 'Capacity formula',
        capacityLabel: 'Available capacity (hours)',
        capacity:
          '= (Headcount × (Working days − OOF days)) × Productive hours per day × Availability factor',
        utilisationLabel: 'Utilisation %',
        utilisation: '= Demand hours ÷ Available capacity hours × 100',
        exampleLabel: 'Example ({{role}}, {{period}})',
        example:
          ': {{headcount}} × {{days}} available days = {{fteDays}} FTE-days → {{fteDays}} × {{hours}} h × {{availability}} = {{capacity}} h',
        privacyNote:
          'Planning granularity is role / region / period. Individual employee-level tracking is deliberately out of scope for privacy reasons.',
      },
      // The two row actions. They differ in one respect only: whether the role,
      // region and effective period are the record's (edit) or the writer's
      // (copy) — see the register's own note on why that distinction is what
      // keeps an edit from becoming a duplicate.
      edit: 'Edit',
      copyToNewPeriod: 'Copy to new period',
      // The editor. Nothing is ever updated in place: every save records a new
      // version carrying a change reason (R3.3), and the recalculation warning
      // (R3.6) plus any capacity shortfall are confirmed before it persists.
      dialog: {
        createTitle: 'Add capacity period',
        copyTitle: 'New capacity period, copied',
        copyNote:
          'These figures are copied from an existing period. Set the role, region and effective period this new entry applies to — as they stand they still name the period they were copied from.',
        editTitle: 'Edit capacity period',
        editNote:
          'Saving records these figures as a new version of this period. The previous version is kept unchanged, so past calculations stay reproducible.',
        editScopeHeading: 'Editing',
        editScopeValue: '{{role}} · {{region}} · {{start}} → {{end}} (v{{version}})',
        editScopeHint:
          'Role, region and the effective period identify this record and cannot be changed here. Use “Copy to new period” to record a different period.',
        versioningNote:
          'Capacity periods are never edited in place. Saving records a new version for this role and region; earlier versions are kept so past calculations stay reproducible.',
        selectRegion: 'Select a region…',
        start: 'Effective from',
        end: 'Effective to',
        // The accessible name for the `DateRangeField` trigger button.
        dateRangeLabel: 'Effective from and to date',
        endBeforeStart: 'The end date must be on or after the start date.',
        headcountHint: 'Leave blank if the headcount is not agreed yet; the period counts as incomplete.',
        availabilityHint: 'A factor between 0.01 and 1.00, for example 0.80 for 80%.',
        changeReason: 'Reason for this change',
        changeReasonHint: 'Recorded against this version and shown in the audit trail.',
        preview: 'This entry yields {{value}} capacity hours.',
        previewIncomplete: 'Capacity hours cannot be derived until headcount, workdays and OOF days are set.',
        rejectedTitle: 'This capacity period was not saved',
        confirmTitle: 'Confirm before saving',
        confirmAndSave: 'Confirm and save',
      },
      // The editor panel beside the register (UI spec §5.3's two-column layout).
      panel: {
        startBlank: 'Start a blank entry',
        cancelEdit: 'Cancel edit',
        unavailableTitle: 'Nothing can be recorded yet',
      },
      role: 'FF role',
      region: 'Region',
      period: 'Effective period',
      periodRange: '{{start}} → {{end}}',
      headcount: 'Headcount',
      workdays: 'Workdays',
      oofDays: 'OOF days',
      productiveHours: 'Productive h/day',
      availability: 'Availability',
      capacityHours: 'Capacity hours',
      actions: 'Actions',
      dataIncomplete: 'No headcount',
      empty: 'No capacity periods recorded for this scope.',
      loadError: 'The capacity periods could not be loaded.',
    },
    effortMatrix: {
      title: 'Effort Matrix',
      subtitle: 'Effort assumptions by activity type, Field Force role and population unit',
      noAccess: 'You do not have access to the effort matrix.',
      filterBarLabel: 'Effort matrix filters',
      tableCaption: 'Effort assumptions by activity type, role and population unit',
      registerHeading: 'Effort matrix',
      registerSubtitle:
        'Minutes per unit by activity type and Field Force role. Admin-maintained and versioned; each row shows the version and the date it takes effect from.',
      edit: 'Edit',
      // ── POS master data (R4.2, R4.3, R9.4) ────────────────────────────────
      pos: {
        heading: 'POS master data (OneData)',
        segmentsHeading: 'Store types / POS segments',
        segmentsCaption: 'POS segments with their sourced counts and source timestamps',
        sourceRefresh: 'Source refresh: {{timestamp}}',
        noSourceRefresh: 'No count has been sourced yet.',
        segmentsEmpty:
          'No POS segments are configured yet. Counts appear here once the OneData import has run.',
        colSegment: 'Segment',
        colPosCount: 'POS count',
        colSourceTimestamp: 'Source timestamp',
        overridesHeading: 'Active POS overrides',
        overridesSubtitle:
          'Manual entries are marked, require a reason and survive a master-data refresh per the defined precedence rule.',
        overridesCaption: 'Active population overrides with their derived values and reasons',
        overridesEmpty: 'No population count is currently overridden.',
        overridesError: 'The active overrides could not be loaded.',
        colInitiative: 'Initiative',
        colDerived: 'Derived',
        colOverride: 'Override',
        colReason: 'Reason',
        calculationNote:
          'The calculation view always shows the selected store types, the derived count, the override value, its reason and the master-data source timestamp.',
      },
      filterActivityType: 'Activity type',
      filterRole: 'FF role',
      allActivityTypes: 'All activity types',
      allRoles: 'All roles',
      showAllVersions: 'Show all versions',
      superseded: '(superseded)',
      deactivated: '(deactivated)',
      add: 'Add assumption',
      // A combination is (activity type, FF role, population unit), so two of the
      // three must exist as master data before one can be recorded.
      needsMasterData:
        'Add a Field Force activity type and a population unit in Administration \u2192 Master data first.',
      newVersion: 'New version',
      dialog: {
        createTitle: 'Add effort assumption',
        versionTitle: 'New version of this effort assumption',
        versioningNote:
          'Effort assumptions are never edited in place. Saving records a new version of this activity type, role and population unit; earlier versions are kept so past calculations stay reproducible.',
        selectActivityType: 'Select an activity type\u2026',
        selectPopulationUnit: 'Select a population unit\u2026',
        effectiveFromHint: 'Demand calculated on or after this date uses this version.',
        effortHint: 'Minutes of effort per population unit, per occurrence.',
        effortOutOfRange: 'Must be between {{min}} and {{max}} minutes.',
        fixedOverheadHint:
          'Optional hours added once per effort assumption, independent of the population count.',
        rejectedTitle: 'This effort assumption was not saved',
        confirmTitle: 'Confirm before saving',
        confirmAndSave: 'Confirm and save',
      },
      activityType: 'Activity type',
      role: 'FF role',
      populationUnit: 'Population unit',
      effortPerUnit: 'Effort/unit (min)',
      fixedOverhead: 'Fixed overhead (h)',
      version: 'Version',
      effectiveFrom: 'Effective from',
      actions: 'Actions',
      empty: 'No effort assumptions recorded. Demand cannot be calculated without them.',
      loadError: 'The effort assumptions could not be loaded.',
      versioningHeading: 'Versioning',
      versioningBody:
        'An effort assumption is never edited in place: each submission creates a new version effective from the date given. Calculations already recorded stay tied to the version that was effective when they ran, so a historical figure can always be reproduced.',
    },
  },
  bar: {
    status: 'Status: {{status}}',
    salesCycles: 'Sales cycles: {{count}}',
    noSalesCycle: 'No sales cycle',
    priority: 'Priority: {{priority}}',
    /** An elevated priority names its value and says so: "Critical (elevated)". */
    priorityValueElevated: '{{priority}} (elevated)',
    /**
     * Fallbacks for a payload cached by a build that carried no priority name.
     * A live response always names the value, so these are rarely seen.
     */
    priorityElevated: 'Elevated',
    priorityNormal: 'Normal',
    confidential: 'Confidential',
    continuesBeyondYear: 'Continues beyond the visible year',
    accessibleName: '{{title}}, status {{status}}',
    accessiblePriority: 'priority {{priority}}',
    accessibleElevated: 'elevated priority',
    accessibleConfidential: 'confidential',
  },

  milestone: {
    fallbackLabel: 'Milestone',
    accessibleName: '{{label}} milestone, {{date}}',
    accessibleNameCompleted: '{{label}} milestone, {{date}}, completed',
    completed: 'Completed',
    planned: 'Planned',
    // The swimlane marker names its parent Initiative so a milestone in the
    // shared strip can be traced to the bar it wires to, without colour.
    swimlaneAccessibleName: '{{label}} milestone for {{initiative}} ({{ref}}), {{date}}',
    swimlaneAccessibleNameCompleted:
      '{{label}} milestone for {{initiative}} ({{ref}}), {{date}}, completed',

    // Cluster translations for when multiple milestones share the same date
    clusterAccessibleName_one: '{{count}} milestone on {{date}}',
    clusterAccessibleName_other: '{{count}} milestones on {{date}}',
    clusterTitle_one: '{{count}} Milestone on {{date}}',
    clusterTitle_other: '{{count}} Milestones on {{date}}',
    closeCluster: 'Close milestone list',
  },

  laneOverflow: {
    collapse: 'Collapse to the row limit',
    expand_one: '{{count}} more not shown — expand',
    expand_other: '{{count}} more not shown — expand',
    collapseAria: 'Collapse this lane to the configured row limit',
    expandAria_one: 'Show {{count}} more initiative in this lane',
    expandAria_other: 'Show {{count}} more initiatives in this lane',
  },

  status: {
    draft: 'Draft',
    submitted: 'Submitted',
    planned: 'Planned',
    approved: 'Approved',
    final: 'Final',
    cancelled: 'Cancelled',
  },

  /**
   * Three-letter status abbreviations for Calendar_View bars too narrow to spell the
   * status out (UI spec §4.2).
   *
   * Chosen, not truncated: `slice(0, 3)` of the words above would give both
   * Submitted and... nothing else, but it collides the moment a translator supplies
   * words that share a prefix, and it cannot be relied on in any language. Keep all
   * six distinct within a locale.
   */
  statusShort: {
    draft: 'DRF',
    submitted: 'SUB',
    planned: 'PLN',
    approved: 'APR',
    final: 'FIN',
    cancelled: 'CNC',
  },

  /**
   * Single-letter status marks for bars at the R10.4 24px minimum, where even three
   * letters will not fit (UI spec §4.2).
   *
   * These must stay mutually distinct within a locale — the calendar has no other
   * status signal left at this width apart from the hatch density, and the tooltip.
   */
  statusLetter: {
    draft: 'D',
    submitted: 'S',
    planned: 'P',
    approved: 'A',
    final: 'F',
    cancelled: 'C',
  },

  /** Bulk_Edit: selection, preview and apply (R10.1-R10.15). */
  bulkEdit: {
    // The action bar above the list.
    barSelected: '{{count}} initiative(s) selected',
    clearSelection: 'Clear selection',
    open: 'Bulk edit',

    title: 'Bulk edit initiatives',
    selected: 'Editing {{count}} selected initiative(s).',
    field: 'Field',
    // R10.1's five low-risk fields, and no others: everything else needs the per-record
    // validation and the reason a single edit carries.
    fieldLabel: {
      priorityId: 'Priority',
      categoryId: 'Category',
      initiativeTypeId: 'Initiative type',
      ownerUserId: 'Owner',
      backupOwnerUserId: 'Backup owner',
    },
    value: 'New value',
    choose: 'Choose a value…',
    clearValue: '(clear the value)',
    emptyValue: '(none)',
    preview: 'Preview changes',
    previewing: 'Previewing…',
    // R10.15: the apply carries the token the preview issued, so there is nothing to
    // confirm until a preview has been read.
    previewHint: 'Choose a field and a value, then preview to see exactly what would change.',
    summary: '{{changed}} would change · {{unchanged}} already match · {{rejected}} rejected',
    col: {
      initiative: 'Initiative',
      before: 'Before',
      after: 'After',
      approvalFlag: 'Approval',
      reason: 'Reason',
    },
    // R10.12: applying marks an approved record as changed after approval.
    willFlag: 'Will flag as changed after approval',
    rejectionsCaption: 'These initiatives would not be changed',
    // R10.5: no reference travels for a record the requester may not read.
    withheldReference: '(not readable)',
    rejection: {
      not_readable: 'You may not read this initiative',
      cancelled: 'Cancelled initiatives are not edited in bulk',
      deactivated_value: 'The chosen value is deactivated',
      validation_failed: 'A validation rule would not be met',
      version_conflict: 'The initiative changed while you were previewing',
    },
    overCap: {
      title: 'Too many initiatives selected',
      // R10.6: both numbers, so narrowing the selection is an action rather than a guess.
      body: 'You selected {{requested}} initiatives and the configured maximum is {{max}}. Narrow the selection and preview again.',
    },
    stale: {
      title: 'The preview is out of date',
      // R10.9 and R10.15 share one remedy: preview again. Saying "try again" would invite
      // an identical failure.
      body: 'One or more initiatives changed since the preview, so nothing was applied. Preview again to see the current values.',
    },
    // R10.14: recorded against the run and every affected record's history.
    reasonLabel: 'Reason for this change',
    // `reasonSchema` is 1 to 500 (`REASON_MIN_LENGTH`), the same bound the cancel
    // dialog states. The hint said 10, which no schema enforces.
    reasonHint: '1 to 500 characters. Recorded against every initiative this changes.',
    apply: 'Apply to {{count}} initiative(s)',
    applying: 'Applying…',
    cancel: 'Cancel',
  },

  /** The Watchouts tab of the Detail_Panel and its two dialogs (R7.1, R7.4, R7.8, R7.9). */
  watchout: {
    add: 'Add watchout',
    edit: 'Edit',
    resolve: 'Resolve',
    empty: 'No watchouts recorded.',
    loadError: 'The watchouts could not be read.',
    col: {
      category: 'Category',
      description: 'Concern',
      severity: 'Severity',
      owner: 'Owner',
      dueDate: 'Due',
      state: 'State',
      actions: 'Actions',
    },
    // R7.2: derived from the resolution timestamp alone.
    state: { open: 'Open', resolved: 'Resolved' },
    resolvedBy: 'Resolved by {{who}} on {{when}}',
    field: {
      category: 'Category',
      severity: 'Severity',
      description: 'Concern',
      descriptionHint: 'What the concern is, in up to 1,000 characters.',
      dueDate: 'Due date (optional)',
      resolutionNote: 'Resolution note (optional)',
      resolutionNoteHint: 'How the concern was addressed, in up to 1,000 characters.',
    },
    addDialog: { title: 'Add watchout' },
    editDialog: { title: 'Edit watchout' },
    resolveDialog: {
      title: 'Resolve watchout',
      // R7.8: retained, not deleted. Said plainly, because "resolve" could reasonably be
      // read as "remove".
      body: 'The watchout is kept with its resolution recorded against it, so the concern and its closure both stay readable.',
    },
    dialog: {
      save: 'Save',
      saving: 'Saving…',
      resolve: 'Resolve',
      resolving: 'Resolving…',
      cancel: 'Cancel',
      // R7.1 requires a category, and R7.3 makes categories master data: with none
      // configured there is nothing valid to submit.
      noCategories:
        'No watchout categories are configured. An administrator can add them under Administration → Master data.',
    },
  },

  /** The three Watchout severities of R7.1, in the domain's ascending rank order. */
  watchoutSeverity: {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
  },

  /** The two dependency functions of Foundation R12. */
  dependencyFunction: {
    dte: 'DTE',
    csc: 'CSC',
  },

  fieldForceRole: {
    ade: 'ADE',
    bde: 'BDE',
    // Not a role anyone holds: the label of the R5.2 series that reads ADE and BDE
    // together. Spelled out rather than "ADE + BDE" because it sits in a chart legend
    // beside the two it combines, where the arithmetic is already visible.
    combined: 'Combined',
  },

  /** R8.3's five Milestone_Readiness states, as the R5.4 funnel labels them. */
  readinessState: {
    completed: 'Completed',
    planned: 'Planned',
    due: 'Due soon',
    overdue: 'Overdue',
    // "No date recorded" rather than "Missing": the milestone is required and absent,
    // and "Missing" alone reads as though the record were lost rather than never set.
    missing: 'No date recorded',
  },

  month: {
    // Title-case abbreviations, for running text and table cells. `short` below is
    // the all-caps form the calendar track and the utilisation strip use as micro
    // labels, where the surrounding type is uppercase anyway; reusing it in a
    // normal-case table cell reads as shouting.
    abbr: {
      1: 'Jan',
      2: 'Feb',
      3: 'Mar',
      4: 'Apr',
      5: 'May',
      6: 'Jun',
      7: 'Jul',
      8: 'Aug',
      9: 'Sep',
      10: 'Oct',
      11: 'Nov',
      12: 'Dec',
    },
    short: {
      1: 'JAN',
      2: 'FEB',
      3: 'MAR',
      4: 'APR',
      5: 'MAY',
      6: 'JUN',
      7: 'JUL',
      8: 'AUG',
      9: 'SEP',
      10: 'OCT',
      11: 'NOV',
      12: 'DEC',
    },
    long: {
      1: 'January',
      2: 'February',
      3: 'March',
      4: 'April',
      5: 'May',
      6: 'June',
      7: 'July',
      8: 'August',
      9: 'September',
      10: 'October',
      11: 'November',
      12: 'December',
    },
  },

  week: {
    fullLabel: 'Week {{week}}, {{year}}',
  },

  export: {
    // The 21 R15.1 filtered-view column labels; the single source the workbook
    // generator's column headers mirror (design.md, R15.1, R18.9).
    column: {
      initiative_id: 'Initiative ID',
      title: 'Title',
      owner: 'Owner',
      backup_owner: 'Backup Owner',
      start_date: 'Start date',
      end_date: 'End date',
      planning_year: 'Planning year',
      sales_cycles: 'Sales cycles',
      primary_platform: 'Primary platform',
      additional_platforms: 'Additional platforms',
      channels: 'Channels',
      audiences: 'Audiences',
      category: 'Category',
      initiative_type: 'Initiative type',
      status: 'Status',
      priority: 'Priority',
      confidential: 'Confidential',
      field_force_involvement: 'Field Force involvement',
      dte_involvement: 'DTE involvement',
      csc_involvement: 'CSC involvement',
    },
  },

  // The Export centre screen (task 23.5, R14.9/R14.11, R15.9, R18.4, R18.17).
  // A scoped block so this task's strings stay additive and self-contained; the
  // singular `export.column.*` block above is the workbook column catalogue and
  // is untouched here.
  exportCentre: {
    page: {
      title: 'Reports & Exports',
      subtitle: 'Filter-aware exports with retained source context',
    },
    heading: 'Export centre',
    hint: 'Exports inherit the current permission and filter context and are written to the export log.',
    context: {
      // The permission/filter context chips (§3.5 / Reports screen). Only the
      // Foundation-relevant chips render; the click dummy's assumption-set chip is
      // out of scope (§0.1, manpower).
      year: 'Year {{year}}',
      scope: 'Permission scope: your access',
    },
    table: {
      colFormat: 'Format',
      colContent: 'Content',
      colControls: 'Controls carried into the file',
      // The action column has no visible header text in the reference; a
      // screen-reader label names it so the column is still announced (§3.5, R18.6).
      colAction: 'Action',
      empty: 'No export formats are available to you.',
    },
    fullBackup: {
      format: 'Excel — full backup',
      content:
        'Initiatives, associations, milestones, dependencies, Field Force scope, Sales Cycle definitions and the master data you may read',
      controls: 'Timestamp, export schema version, permission scope',
    },
    filteredView: {
      format: 'Excel — filtered view',
      content: 'Rows matching the current permitted filter context',
      controls: 'Filter summary, orientation, sort order, generated-on date',
    },
    action: {
      generate: 'Generate',
      generating: 'Starting…',
      download: 'Download',
    },
    // The job life cycle a row shows while polling (R18.4) and on termination.
    status: {
      // Progress display while queued/running (R18.4). `{{percent}}` is the job's
      // reported progress.
      preparing: 'Preparing your export… {{percent}}%',
      queued: 'Queued…',
      // Success: the file is ready to download (R14.11 — a file exists only here).
      ready_one: 'Ready — {{count}} initiative exported.',
      ready_other: 'Ready — {{count}} initiatives exported.',
      readyNoCount: 'Your export is ready to download.',
      // The no-file-produced path (R14.11, R15.9, R18.17): a failed or timed-out
      // job delivered nothing, and the stored data is unchanged.
      failed:
        'The export did not complete and no file was produced. Your stored data is unchanged. Please try again.',
      timedOut:
        'The export did not complete within the time limit and no file was produced. Your stored data is unchanged. Please try a narrower filter.',
      // The POST to start the job itself failed (network/validation) — again no
      // file, data unchanged.
      startFailed:
        'The export could not be started and no file was produced. Your stored data is unchanged. Please try again.',
    },
    // The download endpoint that serves a completed job's file does not yet exist
    // in the API (see task 23.5 report). The button targets the documented shape
    // but the click surfaces this notice rather than fabricating a download.
    downloadUnavailable:
      'The download service for completed exports is not yet available. This will be enabled once the export download endpoint ships.',
  },

  // Session-end causes the API emits as a messageKey (R1.5), resolved here.
  session: {
    ended: {
      absoluteTimeout: 'Your session reached its maximum length and was ended. Please sign in again.',
      idleTimeout: 'Your session ended after a period of inactivity. Please sign in again.',
      notAuthenticated: 'You are not signed in. Please sign in to continue.',
    },
  },

  // Every validation-rule `messageKey` the domain/API emit on a finding (R8.9,
  // R18.9, R18.16). A finding carries the key, never the text; it resolves here.
  validation: {
    title: {
      length: 'The title must be between 1 and 200 characters.',
    },
    owner: {
      missing: 'An owner is required.',
      unknown: 'The selected owner does not exist.',
      inactive: 'The selected owner is no longer active.',
      backupUnknown: 'The selected backup owner does not exist.',
      backupInactive: 'The selected backup owner is no longer active.',
      backupEqualsOwner: 'The backup owner must be someone other than the owner.',
    },
    initiative: {
      cancelledNotEditable:
        'This initiative is cancelled and cannot be edited. Move it back to Draft to make changes.',
      planningYearDerived:
        'The planning year is derived from the start date and cannot be set directly.',
    },
    dateRange: {
      endBeforeStart: 'The end date must be on or after the start date.',
    },
    controlledValue: {
      inactive: 'A selected value is no longer active.',
      unknown: 'A selected value does not exist.',
    },
    // ── Manpower write rules (complan-manpower R2.5, R2.8, R3.6) ───────────
    // The API emits these `messageKey`s on findings; without catalogue entries a
    // rejected Manpower write would show the reader a raw dotted key.
    capacityPeriod: {
      recalculation:
        'Saving this capacity period will recalculate demand and utilisation for every affected scope. Confirm to continue.',
      oofDaysExceedWorkdays: 'Out-of-office days cannot exceed the number of workdays.',
      // Raised when the submitted figures leave the period unable to cover the
      // demand already planned into it. A warning, not a block: recording reduced
      // capacity is legitimate, so the wording says what the consequence will be
      // rather than asking the writer to change the number.
      capacityShortfall:
        'These figures give the period {{capacityHours}} capacity hours against {{demandHours}} hours of demand already planned — {{excessHours}} hours short, at {{utilization}}% utilisation. You can save this; the period will then report as over capacity on Capacity & Utilisation.',
    },
    effortAssumption: {
      recalculation:
        'Saving this effort assumption will recalculate demand for every driver that uses it. Confirm to continue.',
      referencedByDriver:
        'This effort assumption is in use by at least one demand driver, so it cannot be deactivated. Supersede it with a new version instead.',
    },
    confidentiality: {
      changeNotPermitted: 'You are not permitted to change the confidentiality of this Initiative.',
    },
    platformSet: {
      invalid: 'The platform selection is invalid.',
      noPrimaryPlatform: 'Select a primary platform.',
      multiplePrimaryPlatforms: 'Only one platform may be primary.',
      duplicatePlatform: 'A platform is selected more than once.',
      tooManyPlatforms: 'Too many platforms are selected.',
    },
    platformSplit: {
      totalMismatch: 'The platform split must total 100%.',
      incomplete: 'A split value is missing for a selected platform.',
      malformed: 'A platform split value is not a valid number.',
      outOfRange: 'A platform split value is out of the allowed range.',
      tooManyDecimalPlaces: 'A platform split value has too many decimal places.',
    },
    platformColor: {
      malformed: 'The platform colour is not a valid hex colour.',
      duplicateActiveColor: 'Another active platform already uses this colour.',
    },
    salesCycle: {
      noIntersectingCycle: 'No active Sales Cycle intersects the Initiative dates.',
      duplicateNameInPlanningYear: 'A Sales Cycle with this name already exists in the planning year.',
      endDateNotAfterStartDate: 'The Sales Cycle end date must be after its start date.',
      overlap: 'This Sales Cycle overlaps another in the same planning year.',
      gap: 'There is a gap between Sales Cycles in the planning year.',
    },
    salesCycleOverride: {
      emptySelection: 'Select at least one Sales Cycle for the override.',
      inactiveSalesCycle: 'A selected Sales Cycle is no longer active.',
      unknownSalesCycle: 'A selected Sales Cycle does not exist.',
      planningYearNotIntersected: 'A selected Sales Cycle does not intersect the planning year.',
      reasonLength: 'The override reason must be between 1 and 500 characters.',
      tooManyInPlanningYear: 'Too many Sales Cycles are selected for this planning year.',
    },
    platform: {
      primaryMissing: 'A primary platform is required.',
    },
    audience: {
      channelAndAudienceMissing: 'A channel and an audience are required.',
    },
    milestone: {
      mandatoryDateMissing: 'A required milestone date is missing.',
      outOfOrder: 'Milestone dates are out of order.',
    },
    fieldForce: {
      scopeMissing: 'A Field Force scope is required.',
    },
    fieldForceScope: {
      overrideReasonRequired:
        'Replacing the POS-derived population count requires an override reason of 10 to 500 characters.',
      posSegmentUnknown:
        'A selected POS segment no longer exists and was removed. Review the Field Force scope and save again.',
    },
    overlap: {
      sharedPlatformAndAudience:
        'This Initiative overlaps another that shares a platform and audience.',
    },
    masterData: {
      duplicateActiveName: 'Another active record already uses this name.',
      fixedValueSet: 'This value set is fixed and cannot be changed.',
      referencedByInitiatives: 'This record is referenced by one or more Initiatives and cannot be removed.',
    },
    savedView: {
      limitReached: 'You have reached the maximum number of saved views.',
    },
    validationRule: {
      notConfigurable: 'This validation rule is not configurable.',
    },
    context: {
      // The placeholder names are the `detail` keys `IV-001` and `IV-002` emit
      // (`validation-registry.ts`), not free choices: i18next interpolates by name
      // against the finding's own detail, so a mismatch renders the raw `{{...}}`.
      // These read `{{version}}`/`{{timestamp}}` until the live validation endpoint
      // existed to supply the detail, at which point the mismatch became visible.
      masterDataVersion: 'Master data version {{masterDataVersion}}.',
      lastDataRefresh: 'Data last refreshed {{lastDataRefreshAt}}.',
    },
  },

  // The intake validation summary and version-conflict dialog (task 23.2, R5.7,
  // R5.8, R8.2, R8.4, R8.5, R4.11; UI spec §3.8, §6.3). Scoped under
  // `validationSummary.*` so this block is self-contained and additive; the
  // per-rule finding messages themselves resolve from the `validation.*` block
  // above, and the tier glyphs are non-letter symbols carried in component source.
  validationSummary: {
    regionLabel: 'Validation summary',
    allClear: 'No validation issues. This initiative is ready to submit.',
    revalidating: 'Checking…',
    saveDraft: 'Save draft',
    submit: 'Submit for review',
    acknowledge: 'Acknowledge',
    acknowledged: 'Acknowledged',
    inactiveOwnerBadge: 'Inactive owner',
    affectedFields: 'Affected: {{fields}}',
    // A rule that reports once per offending counterpart resolves to the same
    // sentence every time, so the occurrence is named from the finding's own
    // detail — otherwise four different overlaps read as one message repeated.
    overlapSubject: 'with {{ref}}',
    overlapSubjectWithRange: 'with {{ref}} ({{range}})',
    tier: {
      hard: 'Must be fixed before saving',
      governance: 'Must be resolved before submitting',
      warning: 'Please review and acknowledge',
      information: 'For your information',
    },
    blockedReason: {
      hard: 'Resolve every hard validation before submitting for review.',
      governance: 'Resolve every governance rule before submitting for review.',
      warning: 'Acknowledge every warning before submitting for review.',
    },
    conflict: {
      title: 'This initiative changed elsewhere',
      body: 'Another user saved this initiative while you were editing. Their version {{storedVersion}} is now stored; your version {{submittedVersion}} was not saved.',
      keepNote: 'Your entered values are kept. Review the differences below, then resubmit.',
      colField: 'Field',
      colStored: 'Stored value',
      colSubmitted: 'Your value',
      noFieldDiff: 'No field-level differences were reported.',
      dismiss: 'Keep my values',
    },
    // Labels for the logical field paths a finding or a conflict diff reports.
    // Keyed by the dot-free slug of the path (dots are i18next key separators).
    field: {
      title: 'Title',
      details: 'Details',
      ownerUserId: 'Owner',
      backupOwnerUserId: 'Backup owner',
      startDate: 'Start date',
      endDate: 'End date',
      platforms: 'Platforms',
      channelIds: 'Channels',
      customerGroupIds: 'Customer / partner groups',
      audienceIds: 'Audiences',
      categoryIds: 'Categories',
      initiativeTypeIds: 'Initiative types',
      cscAttributeIds: 'CSC attributes',
      salesCycleAssignment: 'Sales cycle assignment',
      priorityId: 'Priority',
      isConfidential: 'Confidential',
      confidentialityReason: 'Confidentiality reason',
      milestones: 'Milestones',
      dependencies: 'Dependencies',
      fieldForceScope: 'Field Force scope',
    },
  },

  // Sales_Cycle_Timing_Form and impact list (task 23.3, R6.1–R6.10, R7.2).
  // Scoped under `salesCycle.*` so this block stays self-contained and additive.
  salesCycle: {
    page: {
      title: 'Sales Cycle Timing',
      subtitle: 'Planning year {{year}}',
    },
    form: {
      heading: 'Sales cycle definition',
      description: 'Capture the year, name, dates and active state of a sales cycle.',
      planningYear: 'Planning year',
      name: 'Name or number',
      namePlaceholder: 'e.g. SC1 or Spring 2025',
      startDate: 'Start date',
      endDate: 'End date',
      // The accessible name for the `DateRangeField` trigger button.
      dateRangeLabel: 'Start and end date',
      effectiveFrom: 'Effective from',
      isActive: 'Active',
      isActiveHint: 'Inactive cycles stay assigned where already used, but are left out of new proposals.',
      submit: 'Save sales cycle',
      submitting: 'Saving…',
      reset: 'Reset',
      createHeading: 'New sales cycle',
      saved: 'Sales cycle saved.',
    },
    existing: {
      heading: 'Sales cycles this year',
      empty: 'No sales cycle is defined for this year yet.',
      loadError: 'The sales cycles could not be loaded.',
      colName: 'Name',
      colRange: 'Dates',
      colState: 'State',
      active: 'Active',
      inactive: 'Inactive',
      edit: 'Edit',
      selected: 'Editing',
    },
    error: {
      duplicateName: 'A sales cycle named “{{name}}” already exists in {{planningYear}}.',
      invertedRange: 'The end date must be after the start date.',
      valuesRetained: 'Your entered values have been kept so you can correct them.',
      generic: 'The sales cycle could not be saved.',
    },
    warning: {
      heading: 'Please review before saving',
      instruction: 'Acknowledge the warnings below to save this definition.',
      overlapTitle: 'Overlapping sales cycles',
      overlapItem: '“{{firstName}}” overlaps “{{secondName}}” from {{overlapStart}} to {{overlapEnd}}.',
      gapTitle: 'Uncovered dates',
      gapItem: 'No active cycle covers {{start}} to {{end}} in {{planningYear}}.',
      acknowledge: 'I have reviewed these warnings',
      saveAnyway: 'Acknowledge and save',
    },
    impact: {
      heading: 'Reassignment impact',
      description: 'Changing these dates affects the sales cycle assignment of {{count}} initiative.',
      description_one: 'Changing these dates affects the sales cycle assignment of {{count}} initiative.',
      description_other: 'Changing these dates affects the sales cycle assignment of {{count}} initiatives.',
      none: 'No initiative is affected by this date change.',
      preview: 'Preview impact',
      previewing: 'Computing impact…',
      loadError: 'The impact list could not be loaded.',
      colSelect: 'Reassign',
      colInitiative: 'Initiative',
      colCurrent: 'Current sales cycles',
      colProposed: 'Proposed sales cycles',
      colSource: 'Assignment source',
      sourceDerived: 'Derived',
      sourceManual: 'Manual override',
      emptySet: 'None',
      requiresExplicit: 'Excluded from the default selection — select to reassign.',
      apply: 'Apply changes',
      applying: 'Applying…',
      applied: 'Applied. {{count}} initiative reassigned.',
      applied_one: 'Applied. {{count}} initiative reassigned.',
      applied_other: 'Applied. {{count}} initiatives reassigned.',
      selectAll: 'Select all eligible',
      clearSelection: 'Clear selection',
      backToForm: 'Back to definition',
      stalePreview: 'The impact list is out of date. Preview it again before applying.',
    },
  },

  // Master_Data_Administration screen (task 23.6, R16, R8.9, R1.9, R17.6, R17.10;
  // UI spec §3.1/§3.4/§3.5). Scoped under `admin.*` so this block stays
  // self-contained and additive. The per-rule finding messages a write surfaces
  // resolve from the `validation.*` block above; only the screen's own labels live
  // here.
  admin: {
    page: {
      title: 'Administration',
      subtitle: 'Master data, validation rules, roles and operations',
    },
    // The tab bar sections, each gated by its capability (R2.5).
    tab: {
      masterData: 'Master data',
      statusFinality: 'Status',
      validationRules: 'Validation rules',
      roles: 'Role assignment',
      operations: 'Operations',
    },
    // No section is available to the signed-in principal.
    noAccess: 'You do not have access to any administration function.',
    // Shared control words across the section.
    common: {
      create: 'Add record',
      edit: 'Edit',
      save: 'Save',
      cancel: 'Cancel',
      saving: 'Saving…',
      saved: 'Saved.',
      delete: 'Delete',
      deactivate: 'Deactivate',
      activate: 'Activate',
      close: 'Close',
      showInactive: 'Show inactive records',
      loading: 'Loading…',
      loadError: 'This data could not be loaded.',
      active: 'Active',
      inactive: 'Inactive',
      yes: 'Yes',
      no: 'No',
      none: '—',
      required: '*',
    },
    // The nine master data sets (R16.1-R16.3). Labels are the screen's own; the
    // record names themselves are user data and are never keyed.
    set: {
      platform: 'Platforms',
      channel: 'Channels',
      'customer-group': 'Customer & partner groups',
      audience: 'Audiences',
      category: 'Categories',
      'initiative-type': 'Initiative types',
      priority: 'Priorities',
      'milestone-type': 'Milestone types',
      'ff-activity-type': 'Field Force activity types',
      region: 'Regions',
      'population-unit': 'Population units',
      'pos-segment': 'POS segments',
      'watchout-category': 'Watchout categories',
      'csc-attribute': 'CSC attributes',
    },
    masterData: {
      heading: 'Master data',
      hint: 'Create, edit, activate and deactivate the configurable value sets. A referenced record cannot be deleted; deactivate it to withhold it from new selections while keeping existing associations.',
      setLabel: 'Value set',
      table: {
        colCode: 'Code',
        colName: 'Name',
        colOrder: 'Order',
        colEffectiveFrom: 'Effective from',
        colColour: 'Colour',
        colElevated: 'Elevated priority',
        colMandatory: 'Mandatory',
        colCalendarRelevant: 'Calendar relevant',
        colPosCount: 'POS count',
        colSourceTimestamp: 'Source timestamp',
        colReferences: 'References',
        colState: 'State',
        colActions: 'Actions',
        empty: 'No records in this set yet.',
        referencedCount_one: '{{count}} initiative',
        referencedCount_other: '{{count}} initiatives',
      },
      // The record editor dialog.
      editor: {
        createTitle: 'Add {{set}} record',
        editTitle: 'Edit {{set}} record',
        code: 'Code',
        name: 'Name',
        displayOrder: 'Display order',
        effectiveFrom: 'Effective from',
        isActive: 'Active',
        isActiveHint: 'An inactive record stays assigned where already used, but is left out of new selections.',
        colour: 'Bar colour',
        colourHint:
          'Accepts #2f6db5, #2F6DB5, the #abc shorthand or rgb(47, 109, 181); all are stored as lowercase hex. Uniqueness among active platforms is checked on save.',
        elevatedPriorityFlag: 'Elevated priority',
        elevatedPriorityHint: 'Elevated initiatives carry the priority marker on the calendar.',
        calendarRelevanceFlag: 'Calendar relevant',
        isMandatory: 'Mandatory milestone',
        precedingMilestoneType: 'Preceding milestone type',
        precedingNone: 'None',
        posCount: 'POS count',
        posCountHint: 'Number of points of sale in this segment.',
        sourceTimestamp: 'Source timestamp',
        sourceTimestampHint: 'Date when the POS count was last imported (optional).',
      },
      // The delete request (R16.6): always refused, reporting the exact
      // referencing count and offering deactivation.
      deleteDialog: {
        title: 'Delete this record?',
        // The record cannot be deleted while referenced; deactivation is offered.
        referenced_one:
          '{{count}} initiative references this record, so it cannot be deleted. Deactivate it instead to withhold it from new selections while keeping existing associations.',
        referenced_other:
          '{{count}} initiatives reference this record, so it cannot be deleted. Deactivate it instead to withhold it from new selections while keeping existing associations.',
        // The platform holds no delete capability at all, so an unreferenced
        // record is refused the same way, with a zero count.
        unreferenced:
          'Records are never deleted. Deactivate this record to withhold it from new selections while keeping existing associations.',
        deactivateAndClose: 'Deactivate record',
        keep: 'Keep record',
        notFound: 'This record no longer exists.',
      },
    },
    statusFinality: {
      heading: 'Initiative status',
      hint: 'The status set is fixed. Only the display label and the display order of each value can be changed — values cannot be added, removed or deactivated.',
      table: {
        colValue: 'Value',
        colLabel: 'Display label',
        colOrder: 'Display order',
        colActions: 'Actions',
        empty: 'No values.',
      },
      editor: {
        title: 'Edit display of “{{value}}”',
        displayLabel: 'Display label',
        displayOrder: 'Display order',
      },
    },
    validationRules: {
      heading: 'Validation rules',
      hint: 'Configurable rules can have their tier, message and active state changed. Hard rules and information context are fixed and shown for reference only.',
      table: {
        colRule: 'Rule',
        colTier: 'Tier',
        colMessage: 'Message',
        colState: 'State',
        colConfigurable: 'Configurable',
        empty: 'No validation rules registered.',
        fixed: 'Fixed',
        editable: 'Editable',
      },
      tier: {
        hard: 'Hard',
        governance: 'Governance',
        warning: 'Warning',
        information: 'Information',
      },
      messageDefault: '(catalogue default)',
      messagePlaceholder: 'Override message (optional)',
      save: 'Save rule changes',
      saving: 'Saving…',
      saved: 'Rule changes saved.',
      reset: 'Reset',
    },
    roles: {
      heading: 'Role assignment',
      hint: 'Assign roles, approver rights and read scope per user. Only directly assigned roles are listed — a user may hold further roles through Identity Service group membership. A change takes effect on that user’s next request without a sign-out.',
      table: {
        colUser: 'User',
        // "Direct" is load-bearing: this column shows `user_role_assignment` rows
        // only, and a user whose roles come from group mapping legitimately shows
        // none. Labelling it "Roles" invited the reading that the user holds
        // nothing at all.
        colRoles: 'Direct roles',
        colApprover: 'Approver rights',
        colPlatformScope: 'Platform scope',
        colChannelScope: 'Channel scope',
        colActions: 'Actions',
        empty: 'No users to assign.',
        noRoles: 'None assigned directly',
        scopeCount_one: '{{count}} platform',
        scopeCount_other: '{{count}} platforms',
        channelScopeCount_one: '{{count}} channel',
        channelScopeCount_other: '{{count}} channels',
        scopeNone: 'All',
      },
      editor: {
        title: 'Edit roles for {{user}}',
        rolesLegend: 'Directly assigned roles',
        // Explains an empty role set rather than leaving an administrator to infer
        // that the user holds nothing. Group-derived roles cannot be shown here at
        // all: group membership reaches the server only in the affected user's own
        // sign-in token, so this screen has no way to read another user's.
        rolesHint:
          'Roles granted here directly. A user may hold further roles through Identity Service group membership, which is configured in the group mapping and is neither shown nor editable here.',
        approverRights: 'Approver rights',
        approverRightsHint:
          'Permits transitions into Approved, and out of Approved or Final. Granted to the person independently of their roles, so it is kept even when no role is assigned directly.',
        // Advisory, not a validation error: the flag may well be correct while
        // appearing powerless, because the role that makes it usable can arrive
        // through group membership this screen cannot see.
        approverRightsNoWriteRole:
          'None of the roles selected above can change an initiative status. Approver rights will have no effect unless this user also holds {{role}} — including through group membership, which is not shown here.',
        platformScopeLegend: 'Platform read scope',
        channelScopeLegend: 'Channel read scope',
        scopeHint: 'Leave empty for unrestricted read scope.',
      },
      role: {
        read_only_user: 'Read-only user',
        commercial_planning: 'Commercial Planning',
        field_force_leadership: 'Field Force Leadership',
        platform_owner: 'Platform Owner',
        channel_owner: 'Channel Owner',
        approver: 'Approver',
        system_administrator: 'System Administrator',
      },
    },
    operations: {
      heading: 'Operations',
      hint: 'The recorded daily backup runs and the periodic restore verifications.',
      backups: {
        heading: 'Backup runs',
        colScheduled: 'Scheduled',
        colCompleted: 'Completed',
        colStores: 'Covered stores',
        colInitiatives: 'Initiatives',
        colMasterData: 'Master data',
        colChanges: 'Change events',
        colRetainUntil: 'Retain until',
        colOutcome: 'Outcome',
        empty: 'No backup runs recorded.',
        pending: 'In progress',
      },
      verifications: {
        heading: 'Restore verifications',
        colVerified: 'Verified',
        colBackup: 'Backup run',
        colInitiatives: 'Initiatives',
        colMasterData: 'Master data',
        colChanges: 'Change events',
        colOutcome: 'Outcome',
        empty: 'No restore verifications recorded.',
      },
      outcome: {
        succeeded: 'Succeeded',
        partial: 'Partial',
        failed: 'Failed',
      },
      // The admin:backup download (System Administrator + Commercial Planning).
      dataBackup: {
        heading: 'Data backup',
        hint: 'A complete copy of the ComPlan data, confidential initiatives included. It restores with `pnpm backup import` or `deploy.sh restore`. Every download is recorded in the audit log. Keep the file private.',
        download: 'Download data backup',
        downloading: 'Preparing backup…',
        failed: 'The request failed.',
        snapshot: {
          available: 'Pre-restore snapshot from {{date}} ({{count}} rows) is kept here until the next restore.',
          download: 'Download snapshot',
        },
        restore: {
          heading: 'Restore from a backup file',
          hint: 'Replaces all ComPlan data here with the uploaded backup. Users, role assignments and group mappings on this environment are kept. The current data is saved as a pre-restore snapshot first. Both environments must be at the same migration level.',
          fileLabel: 'Backup file',
          check: 'Check backup',
          checking: 'Checking…',
          tooLarge: 'The file is larger than the {{mb}} MB upload limit.',
          refused: 'The backup was refused, nothing changed: {{reason}}',
          reportPreview: 'Dry run: this is what the restore would do. Nothing has changed yet.',
          reportApplied: 'Restore applied.',
          colTable: 'Table',
          colBefore: 'Now',
          colAfter: 'After restore',
          colInBackup: 'In backup',
          pruned: 'Orphaned rows dropped: {{detail}}',
          remapped: 'Role scope remapped: {{detail}}',
          usersKept: 'Users and roles of this environment are kept; users from the backup are added so ownership resolves.',
          confirmLabel: 'Type {{word}} to replace all data',
          apply: 'Restore now',
          applying: 'Restoring…',
          done: 'Restore complete. All screens now show the restored data.',
          badMode: 'Unknown restore mode.',
          fileRequired: 'Choose a backup file.',
          previewRequired: 'Check this file before restoring it.',
          notGzip: 'The file is not a gzipped ComPlan backup.',
        },
      },
    },
    // The findings region shared by the record editor, the colour picker, the
    // fixed-set editor and the rule table: a write the server rejected surfaces
    // its findings here. The finding text resolves from `validation.*`.
    findings: {
      title: 'This change was not saved',
      // The tier glyph prefixes match UI spec §3.8; carried as component source
      // since they are non-letter symbols.
    },
  },
} as const;

export type Catalogue = typeof en;
